// useMorseGame hook – core game logic
import { useState, useEffect, useRef, useCallback } from 'react';
import { MORSE_MAP, LEARNING_ORDER, COMMON_WORDS } from '../constants';
import { Q_CODES, OTHER_CODES, COUNTRIES } from '../data/qsoData';
import { generateCallSign, generateActivity, generateContest, detectCountry } from '../utils';
import { generateQSO } from '../utils/qsoGenerator';
import { useAudioInput } from './useAudioInput';
import { useHaptics } from './useHaptics';
import { useAchievements } from './useAchievements';
import { useMidiInput } from './useMidiInput';
import { COMPREHENSION_DRILLS } from '../data/comprehension';
import { LITERATURE, getSegments, getSegmentQuestions, getLiteratureById } from '../data/literature';
import { fetchLocalWeatherDrill } from '../utils/weatherService';
import { fetchNewsDrill } from '../utils/newsService';

import { useGamepadInput } from './useGamepadInput';
import { useDataSync } from './useDataSync';
import { trackEvent } from '../utils/analytics';

const parseRoute = (path) => {
    // If we have a hash, it's a SPA-style route - prioritize it
    if (typeof window !== 'undefined' && window.location.hash) {
        const hash = window.location.hash.slice(1).split('#')[0];
        if (hash) {
            // Map common aliases
            if (hash === 'privacy' || hash === 'tos') return 'legal';
            return hash;
        }
    }

    if (path === '/game' || path.startsWith('/game/')) {
        const parts = path.split('/');
        return parts.length > 2 ? `game/${parts[2]}` : 'game';
    }
    if (path === '/adventure') return 'adventure';
    if (path === '/stats') return 'stats';
    if (path.startsWith('/settings')) return 'settings/account';
    if (path.startsWith('/literature')) return 'literature_library';
    if (path.startsWith('/knowledge')) return 'knowledge';
    if (path === '/leaderboard') return 'leaderboard';
    if (path === '/midnight-express') return 'midnight_express';
    if (path === '/signal-lamp') return 'signal_lamp';
    if (path === '/telegraph-hangman') return 'telegraph_hangman';
    if (path === '/proqso') return 'proqso';
    if (path === '/lab') return 'lab';
    if (path === '/waterfall') return 'waterfall';
    if (path === '/gamecenter') return 'gamecenter';
    if (path === '/social') return 'social';
    if (path.startsWith('/legal') || path === '/privacy' || path === '/tos') return 'legal';
    return 'menu';
};

const routeToPath = (view) => {
    if (view === 'game') return '/game';
    if (view && view.startsWith('game/')) return `/${view}`;
    if (view === 'adventure') return '/adventure';
    if (view === 'stats') return '/stats';
    if (view === 'literature_library') return '/literature';
    if (view && view.startsWith('settings')) return '/settings';
    if (view === 'leaderboard') return '/leaderboard';
    if (view === 'knowledge') return '/knowledge';
    if (view === 'midnight_express') return '/midnight-express';
    if (view === 'signal_lamp') return '/signal-lamp';
    if (view === 'telegraph_hangman') return '/telegraph-hangman';
    if (view === 'proqso') return '/proqso';
    if (view === 'lab') return '/lab';
    if (view === 'waterfall') return '/waterfall';
    if (view === 'gamecenter') return '/gamecenter';
    if (view === 'social') return '/social';
    if (view && view.startsWith('legal')) return '/legal';
    if (view === 'menu') return '/';
    return '/';
};

export const useMorseGame = (audio, userSettings, onUnlockTrivia, onChallengeUpdate, setUserSettings, onMove, onElementEvent) => {
    // ----- UI / mode state -----
    const [view, setInternalView] = useState(() => {
        if (typeof window !== 'undefined') {
            return parseRoute(window.location.pathname);
        }
        return 'menu';
    });

    // Helper function to get default game mode based on skill level
    const getDefaultGameMode = (skillLevel) => {
        const level = skillLevel || 1;
        switch (level) {
            case 1: return 'guided';      // Learning: Guided Lessons
            case 2: return 'callsigns';   // Improving: Call Signs
            case 3: return 'qso';         // Mastery: Simulated QSO
            case 4: return 'icr_words';   // ICR: Common Words
            default: return 'guided';
        }
    };

    // Default game mode based on skill level
    const [gameMode, setGameModeInternal] = useState(() => {
        if (view && view.startsWith('game/')) {
            return view.split('/')[1];
        }
        if (view === 'proqso') return 'pro_qso';
        if (view === 'signal_lamp') return 'signal_lamp';
        if (view === 'telegraph_hangman') return 'telegraph_hangman';
        if (view === 'constellation_hunter') return 'constellation_hunter';
        if (view === 'midnight_express') return 'midnight_express';

        return getDefaultGameMode(userSettings?.skill_level);
    });

    // Wrap setGameMode to prevent event objects from being set
    const setGameMode = (value) => {
        if (typeof value === 'object' && value !== null && value.nativeEvent) {
            console.error('[setGameMode] ERROR: Attempted to set gameMode to an event object!', value);
            console.trace();
            return;
        }
        setGameModeInternal(value);
    };

    const [inputMode, setInputMode] = useState('paddle'); // 'keyboard' is receive‑only

    // Update game mode when skill level changes (e.g., after assessment)
    useEffect(() => {
        // Only update if we're in menu view and mode is at a default value
        const defaultModes = ['guided', 'callsigns', 'qso', 'icr_words'];
        if (view === 'menu' && defaultModes.includes(gameMode)) {
            const newDefaultMode = getDefaultGameMode(userSettings?.skill_level);
            if (gameMode !== newDefaultMode) {
                setGameMode(newDefaultMode);
            }
        }

        // Special cases for views that have their own dedicated UI components
        const viewToModeMap = {
            'signal_lamp': 'signal_lamp',
            'telegraph_hangman': 'telegraph_hangman',
            'constellation_hunter': 'constellation_hunter',
            'proqso': 'pro_qso',
            'midnight_express': 'midnight_express',
            'signal_word': 'signal_word'
        };

        if (viewToModeMap[view] && gameMode !== viewToModeMap[view]) {
            setGameMode(viewToModeMap[view]);
        }

        // Force keying mode for games that require specialized input and don't work in receive-only mode
        const keyingRequiredViews = ['constellation_hunter', 'midnight_express', 'fist_doctor', 'iambic_squeeze', 'signal_word'];
        if (keyingRequiredViews.includes(view) && inputMode === 'keyboard') {
            setInputMode('paddle');
        }
    }, [userSettings?.skill_level, view, inputMode]);







    const vibrate = useHaptics(userSettings.hapticsEnabled);
    // Moved up to be accessible by registerSignal
    const paddleState = useRef({ left: false, right: false });
    const [playbackMode, setPlaybackMode] = useState('char'); // 'char' | 'sequence'
    const [wpm, setWpm] = useState(20);
    const [farnsworthWpm, setFarnsworthWpm] = useState(20);
    const [noiseLevel, setNoiseLevel] = useState(0);
    const [qsbLevel, setQsbLevel] = useState(0);

    const [callSignRegion, setCallSignRegion] = useState(userSettings.country || 'US');
    const [activityType, setActivityType] = useState('POTA');
    const [activityRole, setActivityRole] = useState('HUNTER');
    const [contestType, setContestType] = useState('CQWW_SO');
    const [qsoRole, setQsoRole] = useState('responder');
    const [currentQSO, setCurrentQSO] = useState(null);
    const [qsoStep, setQsoStep] = useState(0);
    const [customChars, setCustomChars] = useState('');
    const [comprehensionItem, setComprehensionItem] = useState(null);
    const [comprehensionState, setComprehensionState] = useState('listening'); // 'loading' | 'listening' | 'answering' | 'result'
    const [comprehensionMode, setComprehensionMode] = useState('literature'); // 'stories' | 'news' | 'wx' | 'random' | 'literature'
    const [currentLiteratureId, setCurrentLiteratureId] = useState(null);
    const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
    const [currentSegmentQuestionIndex, setCurrentSegmentQuestionIndex] = useState(0);
    const [instantText, setInstantText] = useState(false);
    const [isMuteOutgoing, setIsMuteOutgoing] = useState(userSettings?.mute_outgoing || false);

    // Sync mute setting changes
    const setIsMuteOutgoingWithSave = (value) => {
        const newValue = typeof value === 'function' ? value(isMuteOutgoing) : value;
        setIsMuteOutgoing(newValue);
        if (setUserSettings) {
            setUserSettings(prev => ({ ...prev, mute_outgoing: newValue }));
        }
    };

    // Sync from userSettings to local state
    useEffect(() => {
        if (userSettings?.mute_outgoing !== undefined) {
            setIsMuteOutgoing(userSettings.mute_outgoing);
        }
    }, [userSettings?.mute_outgoing]);

    const lastPlayedQsoStep = useRef(-1);
    const IMMORTAL_MODES = ['practice', 'fist_doctor', 'iambic_squeeze', 'lab', 'constellation_hunter', 'signal_lamp', 'adventure', 'pro_qso', 'midnight_express'];

    // Sync instantText with input/game mode
    // Sync instantText with input/game mode - DISABLED (Default to OFF always)
    /*
    useEffect(() => {
        if (gameMode === 'icr_echo' || gameMode === 'icr_words') {
            setInstantText(false);
        } else {
            setInstantText(inputMode !== 'keyboard');
        }
    }, [inputMode, gameMode]);
    */

    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [health, setHealth] = useState(100);

    // ----- Persistent Progress State -----
    const [progress, setProgress] = useState(() => {
        const saved = localStorage.getItem('morse_progress_v2');
        const parsed = saved ? JSON.parse(saved) : {};
        // Add defaults if missing
        if (parsed.level === undefined) parsed.level = 1;
        if (parsed.history === undefined) parsed.history = [];
        if (parsed.totalSessions === undefined) parsed.totalSessions = 0;
        if (parsed.accurateSessions === undefined) parsed.accurateSessions = 0;
        if (parsed.totalCharsCopied === undefined) parsed.totalCharsCopied = 0;
        if (parsed.callsignCount === undefined) parsed.callsignCount = 0;
        if (parsed.paddleCharsSent === undefined) parsed.paddleCharsSent = 0;
        if (parsed.totalQSOs === undefined) parsed.totalQSOs = 0;
        if (parsed.qsbSessions === undefined) parsed.qsbSessions = 0;

        return parsed;
    });

    // persist progress
    useEffect(() => {
        localStorage.setItem('morse_progress_v2', JSON.stringify(progress));
    }, [progress]);

    // Data Sync Hook
    const {
        saveActivity,
        saveLiteratureProgress,
        loadAllLiteratureProgress,
        saveAchievement,
        saveTriviaUnlock,
        saveQSOLog
    } = useDataSync(userSettings, setUserSettings, progress, setProgress);

    const [practiceText, setPracticeText] = useState('');
    const [pileupCount, setPileupCount] = useState(3);
    const [pileupTargets, setPileupTargets] = useState([]);

    const [targetInternal, setTargetInternal] = useState(null); // current character to copy

    // Wrap setTarget to prevent event objects from being set
    const setTarget = (value) => {
        if (typeof value === 'object' && value !== null && value.nativeEvent) {
            console.error('[setTarget] ERROR: Attempted to set target to an event object!', value);
            console.trace();
            return;
        }
        if (typeof value === 'object' && value !== null && value.tagName) {
            console.error('[setTarget] ERROR: Attempted to set target to a DOM element!', value);
            console.trace();
            return;
        }
        setTargetInternal(value);
    };
    const target = targetInternal;

    const [input, setInput] = useState('');
    const [feedback, setFeedback] = useState('neutral'); // 'neutral', 'success', 'error'
    const [levelUpMessage, setLevelUpMessage] = useState(null);
    const [trivia, setTrivia] = useState(null);

    // Initialize Achievements Hook
    const { checkTimeAchievements, checkProgress: checkAchievementProgress } = useAchievements(
        userSettings,
        setUserSettings,
        (achievement) => {
            setLevelUpMessage(`Achievement Unlocked: ${achievement.name}!`);
            audio.playLevelUp();
            setTimeout(() => setLevelUpMessage(null), 4000);
        },
        (streak) => {
            if (onChallengeUpdate) onChallengeUpdate({ type: 'streak', amount: streak });
        },
        saveAchievement
    );

    // Check time-based achievements on mount and every minute
    useEffect(() => {
        checkTimeAchievements();
        const interval = setInterval(checkTimeAchievements, 60000); // Check every minute
        return () => clearInterval(interval);
    }, [checkTimeAchievements]);

    // Check health-based achievements (Comeback Kid)
    useEffect(() => {
        checkAchievementProgress('health_update', { health });
    }, [health, checkAchievementProgress]);

    const [currentSequence, setCurrentSequence] = useState('');
    const [currentMeaning, setCurrentMeaning] = useState('');
    const [sequenceIndex, setSequenceIndex] = useState(0);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [playbackIndex, setPlaybackIndex] = useState(-1);

    // Code Quiz state
    const [quizQuestion, setQuizQuestion] = useState(''); // The definition/meaning to display
    const [quizAnswer, setQuizAnswer] = useState(''); // The correct code
    const [quizInput, setQuizInput] = useState(''); // User's keyed input for the code
    const [quizInputMode, setQuizInputMode] = useState('keyboard'); // 'keyboard' or 'morse'

    const [recentChars, setRecentChars] = useState([]);
    const decodingTimeout = useRef(null);

    // ----- Session & Activity Tracking -----
    const sessionStart = useRef(null);
    const sessionStats = useRef({ total: 0, correct: 0 });
    const characterStats = useRef({});
    const sessionGameMode = useRef(gameMode);
    const viewRef = useRef(view);
    const previousView = useRef(null); // Must be null to detect initial game entry
    const lastActivitySave = useRef(0); // Timestamp of last save to prevent double-saving
    const latestSessionState = useRef({ wpm: 20, score: 0, mode: gameMode, inputMode: 'paddle' });
    const perfectStreak = useRef(0);
    const audioFinishTime = useRef(0);
    const isQsoCompleting = useRef(false);

    useEffect(() => { viewRef.current = view; }, [view]);

    // Sync latest session state for saving on unmount
    useEffect(() => {
        latestSessionState.current = { wpm, score, mode: gameMode, inputMode };
        // If we are currently in an active session, keep the captured mode in sync
        if (sessionStart.current) {
            sessionGameMode.current = gameMode;
        }
    }, [wpm, score, gameMode, inputMode]);

    // Helper to save session activity (called on view change or unmount)
    const saveSession = useCallback((reason = 'unknown') => {
        if (!sessionStart.current) {
            return; // No active session
        }

        const now = Date.now();
        const duration = (now - sessionStart.current) / 1000;
        const timeSinceLastSave = (now - lastActivitySave.current) / 1000;

        // Prevent double-saving: skip if we saved within the last 5 seconds
        if (timeSinceLastSave < 5) {
            console.log('[Activity] Skipping save - recent save exists:', { reason, timeSinceLastSave: Math.round(timeSinceLastSave) });
            sessionStart.current = null;
            return;
        }

        if (duration >= 5) {
            const modeToSave = sessionGameMode.current || latestSessionState.current.mode;

            const accuracy = sessionStats.current.total > 0
                ? (sessionStats.current.correct / sessionStats.current.total) * 100
                : 0;

            const { wpm: savedWpm, score: savedScore, inputMode: savedInputMode } = latestSessionState.current;
            const cleanStats = JSON.parse(JSON.stringify(characterStats.current || {}));

            console.log('[Activity] Saving session:', {
                reason,
                mode: modeToSave,
                duration: Math.round(duration),
                score: Math.round(savedScore)
            });

            saveActivity({
                mode: modeToSave,
                input_mode: savedInputMode || 'paddle',
                wpm: Math.round(savedWpm),
                accuracy: Math.round(accuracy),
                duration_seconds: Math.round(duration),
                score: Math.round(savedScore),
                character_stats: cleanStats
            });

            lastActivitySave.current = now;

            trackEvent('game_finish', {
                mode: modeToSave,
                input_mode: savedInputMode || 'paddle',
                wpm: Math.round(savedWpm),
                accuracy: Math.round(accuracy),
                duration_seconds: Math.round(duration),
                score: Math.round(savedScore)
            });
        }
        sessionStart.current = null;
    }, [saveActivity]);

    // setView handles transition logic
    const setView = useCallback((newView) => {
        // Common audio teardown
        audio.stopPlayback();
        setIsAudioPlaying(false);
        setPlaybackIndex(-1);

        // Only clear game-specific state when leaving for menu or gameover
        if (newView === 'menu' || newView === 'gameover') {
            setQsoStep(0);
            setCurrentQSO(null);
            setComprehensionItem(null);
            setComprehensionState('listening');
        }

        // URL update is handled by host components (MorseApp.jsx) via effects
        // to ensure hash-based anchors are preserved.

        setInternalView(newView);
    }, [audio]);

    // Handle Browser Back/Forward
    useEffect(() => {
        const handlePopState = () => {
            const newView = parseRoute(window.location.pathname);
            setInternalView(newView);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // helper to update session stats
    const updateSessionStats = (isCorrect, char, latencyMs = null) => {
        sessionStats.current.total++;
        if (isCorrect) sessionStats.current.correct++;

        // Update character stats
        if (char) {
            if (!characterStats.current[char]) {
                characterStats.current[char] = { seen: 0, correct: 0, latencyBuckets: { instant: 0, recognition: 0, learning: 0 } };
            }
            characterStats.current[char].seen++;
            if (isCorrect) {
                characterStats.current[char].correct++;

                if (latencyMs !== null && latencyMs > 0) {
                    if (latencyMs < 600) characterStats.current[char].latencyBuckets.instant++;
                    else if (latencyMs < 2000) characterStats.current[char].latencyBuckets.recognition++;
                    else characterStats.current[char].latencyBuckets.learning++;
                }
            }
        }

        // Check Verified Speed (60s duration, >90% accuracy)
        const duration = (Date.now() - sessionStart.current) / 1000;
        const accuracy = (sessionStats.current.correct / sessionStats.current.total) * 100;

        if (duration >= 60 && accuracy >= 90) {
            checkProgress('wpm', { wpm });
        }
    };

    // helper to check achievements
    const checkProgress = async (type, data) => {
        // Check Achievements Hook
        checkAchievementProgress(type, data);

        // Update Persistent Stats
        if (type === 'sequence' && data.mode === 'callsigns') {
            setProgress(prev => ({
                ...prev,
                callsignCount: (prev.callsignCount || 0) + (data.amount || 1)
            }));
        }

        if (type === 'accuracy' && data.isCorrect) {
            setProgress(prev => ({
                ...prev,
                totalCharsCopied: (prev.totalCharsCopied || 0) + 1,
                paddleCharsSent: (inputMode === 'paddle' ? (prev.paddleCharsSent || 0) + 1 : (prev.paddleCharsSent || 0))
            }));
        }

        if (type === 'count') {
            if (data.mode === 'icr_echo') {
                setProgress(prev => ({
                    ...prev,
                    echoCompleted: (prev.echoCompleted || 0) + (data.amount || 1)
                }));
            }
        }

        if (type === 'qso_complete') {
            setProgress(prev => ({
                ...prev,
                totalQSOs: (prev.totalQSOs || 0) + 1,
                qsbSessions: qsbLevel > 0 ? (prev.qsbSessions || 0) + 1 : (prev.qsbSessions || 0)
            }));
        }

        // Track QSB Sessions for other modes
        if ((type === 'level_complete' || type === 'pileup_success') && qsbLevel > 0) {
            setProgress(prev => ({
                ...prev,
                qsbSessions: (prev.qsbSessions || 0) + 1
            }));
        }

        // Update Game Score if applicable
        if (type === 'score' && data.amount) {
            setScore(prev => {
                const newScore = prev + data.amount;
                // Sync to ref immediately so session saves don't get stale data
                latestSessionState.current.score = newScore;
                return newScore;
            });
        }

        if (onChallengeUpdate) {
            const result = await onChallengeUpdate({ type, ...data });
            if (result) {
                if (result.dailyCompleted) {
                    setScore(prev => prev + 500);
                    setLevelUpMessage("Daily Challenge Complete! +500");
                    audio.playLevelUp();
                }
                if (result.weeklyCompleted) {
                    setScore(prev => prev + 2000);
                    setLevelUpMessage("Weekly Challenge Complete! +2000");
                    audio.playLevelUp();
                    checkAchievementProgress('weekly_challenge_complete');
                }
            }
        }

        // Track perfect streak for achievements and challenges
        if (type === 'accuracy') {
            if (data.isCorrect) {
                perfectStreak.current += 1;
                // Send streak update to challenge system
                if (onChallengeUpdate) {
                    onChallengeUpdate({ type: 'streak', amount: perfectStreak.current });
                }
            } else {
                perfectStreak.current = 0;
                // Reset streak in challenge system
                if (onChallengeUpdate) {
                    onChallengeUpdate({ type: 'streak', amount: 0 });
                }
            }
        }
    };

    // Keep health at 100 and lives at 3 for immortal modes (Adventure, Practice, etc)
    useEffect(() => {
        if (IMMORTAL_MODES.includes(gameMode)) {
            if (health < 100) setHealth(100);
            if (lives < 3) setLives(3);
        }
    }, [gameMode, health, lives, setHealth, setLives]);

    // Monitor view changes for saving session
    useEffect(() => {
        // 'adventure' is excluded because useAdventure.js handles its own rich activity logging
        // Views can be 'game' or 'game/callsigns', 'game/guided', etc.
        const isGameView = (v) => v === 'game' || v?.startsWith('game/') || v === 'signal_lamp' || v === 'fist_doctor' || v === 'iambic_squeeze';
        const wasInGame = isGameView(previousView.current);
        const isInGame = isGameView(view);

        // If we are LEAVING a game session
        if (wasInGame && !isInGame) {
            saveSession('ViewChange');
        }

        // If we are ENTERING a game session OR we're in a game but no session is active
        // (handles direct URL navigation and React Strict Mode double-invocation)
        if ((!wasInGame && isInGame) || (isInGame && !sessionStart.current)) {
            sessionStart.current = Date.now();
            sessionStats.current = { total: 0, correct: 0 };
            characterStats.current = {};
            sessionGameMode.current = gameMode; // Capture mode on entry
        }

        previousView.current = view;
    }, [view, gameMode, saveSession]);

    // Score-based auto-save: save activity when score increases by 100+ points
    const lastSavedScore = useRef(0);
    useEffect(() => {
        const isGameView = (v) => v === 'game' || v?.startsWith('game/') || v === 'signal_lamp' || v === 'fist_doctor' || v === 'iambic_squeeze';

        if (!isGameView(view) || !sessionStart.current) {
            return; // Not in a game or no session
        }

        const scoreGain = score - lastSavedScore.current;
        const duration = (Date.now() - sessionStart.current) / 1000;

        // Save when score increases by 100+ and session is at least 10 seconds
        if (scoreGain >= 100 && duration >= 10) {
            const modeToSave = sessionGameMode.current || gameMode;
            const accuracy = sessionStats.current.total > 0
                ? (sessionStats.current.correct / sessionStats.current.total) * 100
                : 0;
            const cleanStats = JSON.parse(JSON.stringify(characterStats.current || {}));

            console.log('[Activity] Score-triggered save:', { mode: modeToSave, duration: Math.round(duration), score, scoreGain });

            saveActivity({
                mode: modeToSave,
                input_mode: inputMode || 'paddle',
                wpm: Math.round(wpm),
                accuracy: Math.round(accuracy),
                duration_seconds: Math.round(duration),
                score: Math.round(score),
                character_stats: cleanStats
            });

            // Mark save
            lastActivitySave.current = Date.now();
            lastSavedScore.current = score;

            // Reset session for next chunk
            sessionStart.current = Date.now();
            sessionStats.current = { total: 0, correct: 0 };
            characterStats.current = {};
        }
    }, [view, gameMode, wpm, score, inputMode, saveActivity]);

    // Check for YYZ sequence
    useEffect(() => {
        if (recentChars.length === 3 &&
            recentChars[0] === 'Y' &&
            recentChars[1] === 'Y' &&
            recentChars[2] === 'Z') {
            // YYZ detected! Trigger achievement
            const settings = localStorage.getItem('morse_settings_v1');
            if (settings) {
                const parsed = JSON.parse(settings);
                if (!parsed.yyzKeyed) {
                    parsed.yyzKeyed = true;
                    localStorage.setItem('morse_settings_v1', JSON.stringify(parsed));
                    // Show a special message
                    setLevelUpMessage("🎸 Nice one, eh? YYZ unlocked!");
                    audio.playLevelUp();
                    setTimeout(() => setLevelUpMessage(null), 3000);
                }
            }
            setRecentChars([]); // Reset to allow re-triggering
        }

        // Check for CWO sequence
        if (recentChars.length === 3 &&
            recentChars[0] === 'C' &&
            recentChars[1] === 'W' &&
            recentChars[2] === 'O') {
            // CWO detected! Trigger achievement
            const settings = localStorage.getItem('morse_settings_v1');
            if (settings) {
                const parsed = JSON.parse(settings);
                if (!parsed.cwoKeyed) {
                    parsed.cwoKeyed = true;
                    localStorage.setItem('morse_settings_v1', JSON.stringify(parsed));
                    // Show a special message
                    setLevelUpMessage("📻 Continuous Wave Order unlocked!");
                    audio.playLevelUp();
                    setTimeout(() => setLevelUpMessage(null), 3000);
                }
            }
            setRecentChars([]); // Reset to allow re-triggering
            setRecentChars([]); // Reset to allow re-triggering
        }

        // Check for SOS sequence
        if (recentChars.length === 3 &&
            recentChars[0] === 'S' &&
            recentChars[1] === 'O' &&
            recentChars[2] === 'S') {
            checkProgress('sos');
            setRecentChars([]);
        }
    }, [recentChars]);

    // Update meaning when sequence changes
    // Check for Microphone Achievement
    useEffect(() => {
        if (inputMode === 'mic' && !userSettings.micUsed) {
            // Unlock "Cheese Grater" (Voice of Morse / Mic Used)
            const updated = { ...userSettings, micUsed: true };
            setUserSettings(updated);
            localStorage.setItem('morse_settings_v1', JSON.stringify(updated));

            setLevelUpMessage("🎤 Cheese Grater unlocked!");
            audio.playLevelUp();
            setTimeout(() => setLevelUpMessage(null), 3000);
        }
    }, [inputMode, userSettings, setUserSettings, audio]);

    const keyPressStart = useRef(null);
    const isKeyPressed = useRef(false);
    const registerSignalRef = useRef(null);

    // audio input (mic) handling
    const audioInput = useAudioInput(wpm, (type) => {
        if (registerSignalRef.current) {
            registerSignalRef.current(type);
        }
    });

    // Audio Settings Effect (Noise & QSB)
    useEffect(() => {
        audio.setQsb(qsbLevel);

        if (noiseLevel > 0) {
            audio.setNoise(noiseLevel);
        } else {
            audio.stopNoise();
        }

        // Cleanup on unmount (or when audio engine changes, though unlikely)
        return () => {
            // We generally don't want to stop noise on re-renders, 
            // but setNoise handles restart if called again.
        };
    }, [noiseLevel, qsbLevel, audio]);

    // Initialize audio settings from userSettings on mount
    useEffect(() => {
        if (userSettings.noiseLevel !== undefined && userSettings.noiseLevel !== noiseLevel) {
            setNoiseLevel(userSettings.noiseLevel);
        }
        if (userSettings.qsbLevel !== undefined && userSettings.qsbLevel !== qsbLevel) {
            setQsbLevel(userSettings.qsbLevel);
        }
    }, []); // Run once on mount

    // View/Input Mode Effect (Microphone & cleanup)
    useEffect(() => {
        audioInput.stopListening();

        if (((view === 'game' || view.startsWith('game/')) || view === 'adventure') && inputMode === 'mic') {
            audioInput.startListening();
        }
    }, [view, inputMode, audioInput]);

    // keep Farnsworth <= character speed
    useEffect(() => {
        if (farnsworthWpm > wpm) setFarnsworthWpm(wpm);
    }, [wpm, farnsworthWpm]);

    // auto‑play the target when in receive‑only mode
    useEffect(() => {
        // Exclude icr_echo, icr_words and _full modes - they handle audio playback via setTimeout in startGame and success feedback
        if (gameMode === 'icr_echo' || gameMode === 'icr_words' || (typeof gameMode === 'string' && gameMode.endsWith('_full'))) {
            return;
        }

        if (
            (view === 'game' || view.startsWith('game/')) &&
            inputMode === 'keyboard' &&
            feedback === 'neutral' &&
            !isAudioPlaying &&
            !instantText && // Don't auto-play if Instant mode is on
            !isMuteOutgoing // Don't auto-play if muted
        ) {
            if (playbackMode === 'char' && target) {
                const code = MORSE_MAP[target];
                if (code) {
                    setIsAudioPlaying(true);
                    audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                        setIsAudioPlaying(false);
                        audioFinishTime.current = Date.now();
                    });
                }
            } else if (playbackMode === 'sequence' && sequenceIndex === 0) {
                // Play full sequence at the start
                // If currentSequence is empty (e.g. letters mode), use target as the sequence
                const seqToPlay = currentSequence || target;

                if (seqToPlay) {
                    setIsAudioPlaying(true);
                    setPlaybackIndex(0);
                    audio.playText(seqToPlay, wpm, farnsworthWpm, (idx) => {
                        setPlaybackIndex(idx);
                    }).then(() => {
                        setIsAudioPlaying(false);
                        setPlaybackIndex(-1);
                    });
                }
            }
        }
    }, [view, inputMode, target, playbackMode, sequenceIndex, currentSequence, feedback, wpm, farnsworthWpm, gameMode, instantText, isMuteOutgoing]);


    // QSO Auto-Play for "Them" turns
    useEffect(() => {
        // Only execute when game view is active
        if (!(view === 'game' || view.startsWith('game/'))) return;

        if (gameMode === 'qso' && currentQSO && currentQSO[qsoStep]) {
            const turn = currentQSO[qsoStep];

            if (turn.speaker === 'them' && lastPlayedQsoStep.current !== qsoStep) {

                if (instantText) {
                    // Instant Mode:
                    // 1. Mark as played
                    // 2. Do NOT play audio
                    // 3. Wait for a "reading duration" so the user can see what "They" said
                    // 4. Then advance to 'Your Turn'

                    lastPlayedQsoStep.current = qsoStep;

                    // Calculate reading delay (Base 1.5s + 60ms per char)
                    const text = turn.text || "";
                    const readDelay = 1500 + (text.length * 60);

                    setTimeout(() => {
                        // Advance to next turn (User)
                        const nextStep = qsoStep + 1;
                        console.log('[QSO Instant] Transitioning after "them" turn:', { currentStep: qsoStep, nextStep, totalSteps: currentQSO.length });

                        if (nextStep < currentQSO.length) {
                            setQsoStep(nextStep);
                            const nextTurn = currentQSO[nextStep];

                            console.log('[QSO Instant] Setting up "you" turn:', {
                                speaker: nextTurn.speaker,
                                text: nextTurn.text
                            });

                            setCurrentSequence(nextTurn.text);

                            let first = 0;
                            while (nextTurn.text[first] === ' ' && first < nextTurn.text.length) first++;
                            setSequenceIndex(first);
                            setTarget(nextTurn.text[first]);

                            console.log('[QSO Instant] Set values:', {
                                currentSequence: nextTurn.text,
                                target: nextTurn.text[first],
                                sequenceIndex: first
                            });
                        } else {
                            // End of QSO
                            setLevelUpMessage("QSO Complete! 73!");
                            checkProgress('qso_complete');
                            if (onChallengeUpdate) {
                                onChallengeUpdate({ type: 'sequence', amount: 1, mode: 'qso' });
                            }
                            // audio.playLevelUp(); // Removed to prevent overlap with achievement sounds
                            setTimeout(() => setView('menu'), 3000);
                        }
                    }, readDelay);
                    return;
                }

                lastPlayedQsoStep.current = qsoStep;
                setIsAudioPlaying(true);
                setPlaybackIndex(0);

                // Small delay before starting to ensure UI is ready
                setTimeout(() => {
                    // Double check instantText didn't turn on during delay
                    if (currentQSO[qsoStep]?.speaker === 'them') {
                        audio.playText(turn.text, wpm, farnsworthWpm, (idx) => {
                            setPlaybackIndex(idx);
                        }).then(() => {
                            setIsAudioPlaying(false);
                            setPlaybackIndex(-1);

                            // Auto-advance to next step (Your turn)
                            const nextStep = qsoStep + 1;
                            console.log('[QSO] Transitioning after "them" turn:', { currentStep: qsoStep, nextStep, totalSteps: currentQSO.length });

                            if (nextStep < currentQSO.length) {
                                setQsoStep(nextStep);
                                const nextTurn = currentQSO[nextStep];

                                console.log('[QSO] Setting up "you" turn:', {
                                    speaker: nextTurn.speaker,
                                    text: nextTurn.text,
                                    textLength: nextTurn.text?.length
                                });

                                setCurrentSequence(nextTurn.text);

                                // Setup target for user to send
                                let first = 0;
                                while (nextTurn.text[first] === ' ' && first < nextTurn.text.length) first++;
                                setSequenceIndex(first);
                                setTarget(nextTurn.text[first]);

                                console.log('[QSO] Set values:', {
                                    currentSequence: nextTurn.text,
                                    target: nextTurn.text[first],
                                    sequenceIndex: first
                                });
                            } else {
                                setLevelUpMessage("QSO Complete! 73!");
                                checkProgress('qso_complete');
                                if (onChallengeUpdate) {
                                    onChallengeUpdate({ type: 'sequence', amount: 1, mode: 'qso' });
                                }
                                // audio.playLevelUp();
                                setTimeout(() => setView('menu'), 3000);
                            }
                        });
                    }
                }, 500);
            }
        }
    }, [view, gameMode, currentQSO, qsoStep, wpm, farnsworthWpm, instantText]);

    // Handle Toggling Instant DURING Playback
    useEffect(() => {
        if (instantText && isAudioPlaying) {
            audio.stopPlayback();
            setIsAudioPlaying(false);
            setPlaybackIndex(-1);

            // If we were in a sequence playback (like QSO or receive-only sequence)
            // stopping here typically leaves us in a "valid" state to continue reading.

            // For QSO specifically, if we interrupt "Them" talking, we might want to auto-advance?
            // User said "karaoke should immediately complete". 
            // In a standard sequence, stopping audio + index -1 removes the highlight (completes visual aspect).

            // If in QSO mode and it was 'them' speaking:
            if (gameMode === 'qso' && currentQSO && currentQSO[qsoStep]?.speaker === 'them') {
                // Calculate reading delay to maintain "conversation" feel
                const turn = currentQSO[qsoStep];
                const text = turn.text || "";
                const readDelay = 1500 + (text.length * 60);

                setTimeout(() => {
                    // Check if we are still on the same step (prevent race if user exited)
                    // Note: We can't easily check 'qsoStep' inside timeout without ref, 
                    // but broadly it should be safe in this context as mode reset clears everything.

                    const nextStep = qsoStep + 1;
                    if (nextStep < currentQSO.length) {
                        setQsoStep(nextStep);
                        const nextTurn = currentQSO[nextStep];
                        setCurrentSequence(nextTurn.text);
                        let first = 0;
                        while (nextTurn.text[first] === ' ' && first < nextTurn.text.length) first++;
                        setSequenceIndex(first);
                        setTarget(nextTurn.text[first]);
                    } else {
                        setLevelUpMessage("QSO Complete! 73!");
                        checkProgress('qso_complete');
                        if (onChallengeUpdate) {
                            onChallengeUpdate({ type: 'sequence', amount: 1, mode: 'qso' });
                        }
                        // audio.playLevelUp();
                        setTimeout(() => setView('menu'), 3000);
                    }
                }, readDelay);
            }
        }
    }, [instantText, isAudioPlaying, gameMode, currentQSO, qsoStep]);

    // helper to get the next target character / sequence
    const getNextTarget = useCallback(
        (mode, currentProgress, region, actType, actRole) => {
            if (mode === 'callsigns') return generateCallSign(region);
            if (mode === 'activities') return generateActivity(actType, actRole, userSettings);
            if (mode === 'contests') return generateContest(contestType, userSettings);
            if (mode === 'qcodes') return Q_CODES[Math.floor(Math.random() * Q_CODES.length)].code;
            if (mode === 'othercodes') return OTHER_CODES[Math.floor(Math.random() * OTHER_CODES.length)].code;
            if (mode === 'code_quiz') {
                // Combine both Q-codes and other codes for the quiz
                const allCodes = [...Q_CODES, ...OTHER_CODES];
                const selected = allCodes[Math.floor(Math.random() * allCodes.length)];
                return selected; // Return the full object { code, meaning }
            }
            if (mode === 'icr_words') return COMMON_WORDS[Math.floor(Math.random() * COMMON_WORDS.length)];
            if (mode === 'guided') {
                const level = currentProgress.level || 1;
                const unlocked = LEARNING_ORDER.slice(0, Math.min(level, LEARNING_ORDER.length));
                const newest = unlocked[unlocked.length - 1];

                // If user has mastered all characters (level > length), use a uniform distribution
                if (level > LEARNING_ORDER.length) {
                    return unlocked[Math.floor(Math.random() * unlocked.length)];
                }

                // Standard Koch: 50% bias for the newest character being learned
                return Math.random() < 0.5 ? newest : unlocked[Math.floor(Math.random() * unlocked.length)];
            }
            if (mode === 'icr_echo') {
                const keys = Object.keys(MORSE_MAP).filter(k => /^[A-Z0-9]$/.test(k));
                return keys[Math.floor(Math.random() * keys.length)];
            }
            if (mode === 'custom') {
                const chars = customChars.toUpperCase().replace(/\s/g, '').split('').filter(c => MORSE_MAP[c]);
                if (chars.length === 0) return 'E'; // Fallback
                return chars[Math.floor(Math.random() * chars.length)];
            }
            let keys = [];
            if (mode === 'letters') keys = Object.keys(MORSE_MAP).filter((k) => /^[A-Z]$/.test(k));
            else if (mode === 'numbers') keys = Object.keys(MORSE_MAP).filter((k) => /^[0-9]$/.test(k));
            else if (mode === 'special') keys = Object.keys(MORSE_MAP).filter((k) => /^[^A-Z0-9]$/.test(k));
            else if (mode === 'fist_doctor' || mode === 'iambic_squeeze') {
                keys = Object.keys(MORSE_MAP).filter((k) => /^[A-Z0-9]$/.test(k));
            } else keys = Object.keys(MORSE_MAP);
            if (keys.length === 0) return 'A'; // Ultimate fallback
            return keys[Math.floor(Math.random() * keys.length)];
        },
        [userSettings, contestType, customChars]
    );

    // helper to update meaning based on current sequence
    const updateMeaning = (mode, seq) => {
        if (mode === 'qcodes' || mode === 'qcodes_full') {
            const def = Q_CODES.find(q => q.code === seq);
            setCurrentMeaning(def ? def.meaning : '');
        } else if (mode === 'othercodes' || mode === 'othercodes_full') {
            const def = OTHER_CODES.find(c => c.code === seq);
            setCurrentMeaning(def ? def.meaning : '');
        } else if (mode === 'callsigns' || mode === 'callsigns_full') {
            const countryCode = detectCountry(seq);
            const country = COUNTRIES.find(c => c.code === countryCode);
            setCurrentMeaning(country ? country.label : '');
        } else {
            setCurrentMeaning('');
        }
    };

    // ----- game start -----

    // ----- game start -----
    // ----- game start -----
    const startGame = (modeOverride = null, resetLiterature = true, initialLitId = null, initialSegmentIndex = null) => {
        const mode = (modeOverride && typeof modeOverride === 'string') ? modeOverride : gameMode;
        if (modeOverride && typeof modeOverride === 'string') setGameMode(modeOverride);

        audio.initAudio();
        lastPlayedQsoStep.current = -1;
        isQsoCompleting.current = false;
        setScore(0);
        setLives(3);
        setHealth(100);

        // Reset literature indices when starting a new game session (from menu)
        if (mode === 'icr_comprehension' && (comprehensionMode === 'literature' || comprehensionMode === 'stories' || comprehensionMode === 'poems') && resetLiterature) {
            setCurrentSegmentIndex(initialSegmentIndex !== null ? initialSegmentIndex : 0);
            setCurrentSegmentQuestionIndex(0); // Only reset question index when starting fresh
        }

        if (initialLitId) {
            setCurrentLiteratureId(initialLitId);
            setComprehensionMode('literature');
        }
        setInput('');
        setFeedback('neutral');
        setLevelUpMessage(null);
        audio.stopPlayback();
        setIsAudioPlaying(false);

        if (mode === 'adventure') {
            trackEvent('game_start', { mode: 'adventure' });
            setView('adventure');
            return;
        }

        trackEvent('game_start', {
            mode: mode,
            wpm: wpm,
            inputMode: inputMode
        });

        if (mode === 'constellation_hunter' || mode === 'signal_lamp' || mode === 'fist_doctor' || mode === 'iambic_squeeze' || mode === 'telegraph_hangman' || mode === 'pro_qso' || mode === 'midnight_express') {
            if (mode === 'fist_doctor' || mode === 'iambic_squeeze') {
                setView('game');
            } else {
                setView(mode === 'pro_qso' ? 'proqso' : mode);
            }

            setTarget(null);
            const firstTarget = getNextTarget(mode, progress);
            setTarget(firstTarget);
            console.log('[Special Mode Start] Initializing target:', firstTarget);
            return;
        }

        if (mode === 'pileup') {
            const targets = [];
            // Generate random callsigns based on pileupCount
            for (let i = 0; i < pileupCount; i++) targets.push(generateCallSign());
            setPileupTargets(targets);
            setTarget(null); // Hide target in pileup mode
            setView('game/pileup');

            // Small delay to let view render before playing
            setTimeout(async () => {
                setIsAudioPlaying(true);
                await audio.playPileup(targets, wpm, farnsworthWpm);
                setIsAudioPlaying(false);
            }, 500);
            return;
        }

        if (mode === 'callsigns' || mode === 'qcodes' || mode === 'othercodes' || mode === 'icr_words' || (typeof mode === 'string' && mode.endsWith('_full'))) {
            let sign = '';
            if (mode === 'callsigns' || mode === 'callsigns_full') {
                const regionToUse = callSignRegion || userSettings.country;
                sign = getNextTarget('callsigns', null, regionToUse);
            } else if (mode === 'qcodes_full') {
                sign = getNextTarget('qcodes');
            } else if (mode === 'othercodes_full') {
                sign = getNextTarget('othercodes');
            } else {
                sign = getNextTarget(mode);
            }
            setCurrentSequence(sign);
            updateMeaning(mode, sign);
            setSequenceIndex(0);
            setTarget(sign[0]);

            // Force sequence playback for words to encourage whole-word recognition
            if (mode === 'icr_words' || (typeof mode === 'string' && mode.endsWith('_full'))) {
                setPlaybackMode('sequence');
                // Play full word with increased delay to ensure audio context is ready
                if (!instantText && !isMuteOutgoing) {
                    setTimeout(() => {
                        setIsAudioPlaying(true);
                        audio.playText(sign, wpm, farnsworthWpm, (idx) => {
                            setPlaybackIndex(idx);
                        }).then(() => {
                            setIsAudioPlaying(false);
                            setPlaybackIndex(-1);
                        });
                    }, 1000); // Increased from 500ms to ensure audio context initialization
                }
            } else {
                setPlaybackMode('char');
                // Play first character with delay to ensure audio context is ready
                if (!instantText && !isMuteOutgoing) {
                    setTimeout(() => {
                        const code = MORSE_MAP[sign[0]];
                        if (code) {
                            setIsAudioPlaying(true);
                            audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                                setIsAudioPlaying(false);
                                audioFinishTime.current = Date.now();
                            });
                        }
                    }, 500);
                }
            }
        } else if (mode === 'code_quiz') {
            // Quiz mode: show definition, user keys the code
            const quizItem = getNextTarget('code_quiz');
            setQuizQuestion(quizItem.meaning);
            setQuizAnswer(quizItem.code);
            setQuizInput('');
            setCurrentMeaning(quizItem.meaning); // Display the question
            setTarget('?'); // Always show question mark for quiz

            // In both modes, we want the user to "guess" (key from memory)
            // so we don't set a currentSequence or sequenceIndex
            setCurrentSequence(null);
            setSequenceIndex(0);
        } else if (mode === 'activities') {
            const act = getNextTarget('activities', null, null, activityType, activityRole);
            setCurrentSequence(act);
            setSequenceIndex(0);
            let first = 0;
            // Skip spaces and characters without Morse code mappings
            while (first < act.length && (act[first] === ' ' || !MORSE_MAP[act[first]])) {
                first++;
            }
            setSequenceIndex(first);
            setTarget(act[first]);
            // Play first character with delay to ensure audio context is ready
            if (!instantText && !isMuteOutgoing) {
                setTimeout(() => {
                    const code = MORSE_MAP[act[first]];
                    if (code) {
                        setIsAudioPlaying(true);
                        audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                            setIsAudioPlaying(false);
                            audioFinishTime.current = Date.now();
                        });
                    }
                }, 500);
            }
        } else if (mode === 'contests') {
            const exchange = getNextTarget('contests', null, null, contestType);
            setCurrentSequence(exchange);
            setSequenceIndex(0);
            let first = 0;
            // Skip spaces and characters without Morse code mappings
            while (first < exchange.length && (exchange[first] === ' ' || !MORSE_MAP[exchange[first]])) {
                first++;
            }
            setSequenceIndex(first);
            setTarget(exchange[first]);
            // Play first character with delay to ensure audio context is ready
            if (!instantText && !isMuteOutgoing) {
                setTimeout(() => {
                    const code = MORSE_MAP[exchange[first]];
                    if (code) {
                        setIsAudioPlaying(true);
                        audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                            setIsAudioPlaying(false);
                            audioFinishTime.current = Date.now();
                        });
                    }
                }, 500);
            }
        } else if (mode === 'qso') {
            const userCall = userSettings.callSign || 'MYCALL';
            const script = generateQSO(userCall, qsoRole);
            setCurrentQSO(script || []);
            setQsoStep(0);

            if (!script || script.length === 0) {
                console.warn('[QSO Init] Warning: Generated script is empty!');
                return;
            }

            const turn = script[0];
            console.log('[QSO Init] Starting QSO:', {
                firstSpeaker: turn.speaker,
                firstText: turn.text,
                totalSteps: script.length
            });

            setCurrentSequence(turn.text);

            if (turn.speaker === 'you') {
                let first = 0;
                while (turn.text[first] === ' ' && first < turn.text.length) first++;
                setSequenceIndex(first);
                setTarget(turn.text[first]);

                console.log('[QSO Init] First turn is YOU - set:', {
                    currentSequence: turn.text,
                    target: turn.text[first],
                    sequenceIndex: first
                });
            } else {
                setTarget(null); // Wait for auto-play
                setSequenceIndex(0);
                console.log('[QSO Init] First turn is THEM - waiting for auto-play');
            }
        } else if (mode === 'pro_qso') {
            setView('game');
            setTarget(null);
            return;
        } else if (mode === 'icr_echo') {
            const char = getNextTarget('icr_echo');
            setTarget(char);
            setCurrentSequence('');
            setPlaybackMode('char');
            // Play with increased delay to ensure audio context is ready
            // Using setTimeout to avoid auto-play useEffect re-triggering issues
            if (!instantText) {
                setTimeout(() => {
                    const code = MORSE_MAP[char];
                    if (code) {
                        setIsAudioPlaying(true);
                        audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                            setIsAudioPlaying(false);
                            audioFinishTime.current = Date.now();
                        });
                    }
                }, 1000); // Increased from 500ms to ensure audio context initialization
            }
        } else if (mode === 'icr_comprehension') {
            setComprehensionItem(null);
            setComprehensionState('loading');
            setView('game');

            const startDrill = async () => {
                let item;
                let mode = comprehensionMode;

                console.log('[Comprehension] Starting drill with mode:', mode);

                // Handle 'random' mode
                if (mode === 'random') {
                    const rand = Math.random();
                    if (rand < 0.3) mode = 'wx';
                    else if (rand < 0.6) mode = 'news';
                    else mode = 'literature';
                }

                try {
                    if (mode === 'wx') {
                        item = await fetchLocalWeatherDrill(userSettings.regionCode);
                    } else if (mode === 'news') {
                        item = await fetchNewsDrill();
                    } else if (mode === 'literature' || mode === 'stories' || mode === 'poems') {
                        // Map 'stories' and 'poems' to literature mode
                        // Pick a random piece of literature if not specified
                        let litId = initialLitId || currentLiteratureId;
                        let startSegIndex = initialSegmentIndex !== null ? initialSegmentIndex : (currentSegmentIndex || 0);

                        // If we came from an old 'stories' or 'poems' mode, pick a random one of that type
                        if (!litId || mode === 'stories' || mode === 'poems') {
                            const typeFilter = mode === 'poems' ? 'poem' : mode === 'stories' ? 'story' : null;
                            const filtered = typeFilter ? LITERATURE.filter(l => l.type === typeFilter) : LITERATURE;
                            const choices = filtered.length > 0 ? filtered : LITERATURE;

                            if (choices.length > 0) {
                                litId = choices[Math.floor(Math.random() * choices.length)].id;
                            } else if (LITERATURE.length > 0) {
                                litId = LITERATURE[0].id;
                            }
                        }

                        if (!litId && LITERATURE.length > 0) {
                            litId = LITERATURE[0].id; // Ultimate fallback
                        }

                        console.log('[Comprehension] Selected piece id:', litId);
                        const segments = getSegments(litId);
                        const segment = segments[startSegIndex] || segments[0];

                        if (!segment) {
                            console.error('[Comprehension] No segments found for:', litId);
                            throw new Error(`No segments found for literature id: ${litId}`);
                        }

                        const questions = getSegmentQuestions(litId, segment.id);

                        // Get the chapter/item object (could be a chapter or a standalone piece)
                        const chapterOrItem = getLiteratureById(litId);

                        // For novels, find the parent novel to get cover image and metadata
                        let litInfo = LITERATURE.find(l => l.id === litId);
                        if (!litInfo && chapterOrItem?.type === 'chapter') {
                            // This is a chapter, find the parent novel
                            litInfo = LITERATURE.find(l => l.chapters?.some(ch => ch.id === litId));
                        }

                        item = {
                            id: segment.id,
                            text: segment.morseText, // Use sanitized morseText
                            originalText: segment.text,
                            question: questions[0]?.question || "No question available",
                            options: questions[0]?.options || ["A", "B", "C", "D"],
                            answer: questions[0]?.answer || "A",
                            questions: questions, // Store all questions for this segment
                            literatureTitle: litInfo?.title,
                            literatureAuthor: litInfo?.author,
                            literatureType: litInfo?.type,
                            literatureDescription: litInfo?.description,
                            literatureDate: litInfo?.publicationDate,
                            literatureGenres: litInfo?.genres,
                            literatureCoverImage: litInfo?.coverImage,
                            literatureChapterTitle: chapterOrItem?.type === 'chapter' ? chapterOrItem.title : null,
                            segmentTitle: segment.title,
                            segmentIndex: startSegIndex,
                            totalSegments: segments.length
                        };

                        // Track progress: Mark as started
                        trackLiteratureProgress(litId, 'started');

                        setCurrentLiteratureId(litId);
                        setCurrentSegmentQuestionIndex(0);
                    }
                } catch (e) {
                    console.error('[Comprehension] startDrill error:', e);
                    // Fallback will happen below
                }

                // Default / Fallback to stories
                if (!item) {
                    item = COMPREHENSION_DRILLS[Math.floor(Math.random() * COMPREHENSION_DRILLS.length)];
                }

                setComprehensionItem(item);
                setComprehensionState('listening');
                setCurrentSequence(item.text);
                setTarget(null);

                if (!instantText) {
                    setTimeout(() => {
                        setIsAudioPlaying(true);
                        audio.playText(item.text, wpm, farnsworthWpm).then(() => {
                            setIsAudioPlaying(false);
                            // If segment has no questions, show text review immediately
                            if (item.questions && item.questions.length === 0) {
                                setFeedback('neutral');
                                setComprehensionState('result');
                            } else {
                                setComprehensionState('answering');
                            }
                        });
                    }, 1000);
                } else {
                    // Instant mode: jump straight to answering/result
                    setTimeout(() => {
                        if (item.questions && item.questions.length === 0) {
                            setFeedback('neutral');
                            setComprehensionState('result');
                        } else {
                            setComprehensionState('answering');
                        }
                    }, 500);
                }
            };
            startDrill();
        } else if (mode === 'letters' || mode === 'numbers' || mode === 'special' || mode === 'guided' || mode === 'custom' || mode === 'fist_doctor' || mode === 'iambic_squeeze' || mode === 'practice' || mode === 'copy' || mode === 'words') {

            const nextTarget = getNextTarget(mode, progress);
            setTarget(nextTarget);
            setCurrentSequence('');

            // Play first character audio with delay to ensure audio context is ready
            if (!isMuteOutgoing) {
                setTimeout(() => {
                    const code = MORSE_MAP[nextTarget];
                    if (code) {
                        setIsAudioPlaying(true);
                        audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                            setIsAudioPlaying(false);
                            audioFinishTime.current = Date.now();
                        });
                    }
                }, 500);
            }
        }

        if (mode !== 'fist_doctor' && mode !== 'iambic_squeeze' && mode !== 'adventure' && mode !== 'midnight_express' && mode !== 'telegraph_hangman' && mode !== 'pro_qso' && mode !== 'signal_lamp' && mode !== 'constellation_hunter') {
            setView(`game/${mode}`);
        }
    };

    // --- Specialized View Auto-Start ---
    useEffect(() => {
        if (view === 'fist_doctor' && gameMode !== 'fist_doctor') {
            startGame('fist_doctor', true);
            setInputMode('paddle');
        } else if (view === 'iambic_squeeze' && gameMode !== 'iambic_squeeze') {
            startGame('iambic_squeeze', true);
            setInputMode('paddle');
        } else if (view === 'telegraph_hangman' && gameMode !== 'telegraph_hangman') {
            startGame('telegraph_hangman', true);
        }
    }, [view, gameMode]);

    // ----- playback button (manual) -----
    const handlePlayback = (overrideText = null) => {
        if (isAudioPlaying) {
            audio.stopPlayback();
            setIsAudioPlaying(false);
            return;
        }

        if (gameMode === 'pileup') {
            setIsAudioPlaying(true);
            audio.playPileup(pileupTargets, wpm, farnsworthWpm).then(() => setIsAudioPlaying(false));
            return;
        }

        // Allow explicit override (e.g. from Constellation Hunter)
        if (overrideText && typeof overrideText === 'string') {
            const code = MORSE_MAP[overrideText.toUpperCase()];
            if (code) {
                setIsAudioPlaying(true);
                audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                    setIsAudioPlaying(false);
                    audioFinishTime.current = Date.now();
                });
                return;
            }
        }

        if (playbackMode === 'sequence') {
            const seqToPlay = currentSequence || target;
            if (seqToPlay) {
                if (isMuteOutgoing) {
                    // Just animate clinical markers if muted
                    setPlaybackIndex(0);
                    let idx = 0;
                    const interval = setInterval(() => {
                        idx++;
                        if (idx >= seqToPlay.length) {
                            clearInterval(interval);
                            setPlaybackIndex(-1);
                        } else {
                            setPlaybackIndex(idx);
                        }
                    }, 500); // 500ms per char for visual tracking
                    return;
                }
                setIsAudioPlaying(true);
                setPlaybackIndex(0);
                audio.playText(seqToPlay, wpm, farnsworthWpm, (idx) => {
                    setPlaybackIndex(idx);
                }).then(() => {
                    setIsAudioPlaying(false);
                    setPlaybackIndex(-1);
                });
            }
        } else {
            if (isMuteOutgoing) {
                // Just toggle playing state briefly for UI feedback
                setIsAudioPlaying(true);
                setTimeout(() => setIsAudioPlaying(false), 300);
                return;
            }
            const code = MORSE_MAP[target];
            if (code) {
                setIsAudioPlaying(true);
                audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                    setIsAudioPlaying(false);
                    audioFinishTime.current = Date.now();
                });
            }
        }
    };

    // helper to calculate progress (level up/down)
    const calculateProgress = (prev, success) => {
        const newHistory = [...prev.history, success].slice(-20);
        const recentSuccess = newHistory.filter(Boolean).length;
        const accuracy = newHistory.length > 0 ? recentSuccess / newHistory.length : 0;

        let newLevel = prev.level;
        let newMaxLevel = prev.maxLevel || prev.level;

        // Level Up: 90% accuracy over last 10 attempts
        if (newHistory.length >= 10 && accuracy >= 0.9) {
            newLevel = Math.min(LEARNING_ORDER.length + 1, prev.level + 1);
            newMaxLevel = Math.max(newMaxLevel, newLevel);
            if (newLevel > prev.level) {
                audio.playLevelUp();

                const unlockedChar = LEARNING_ORDER[newLevel - 1];
                if (unlockedChar) {
                    setLevelUpMessage(`Level Up! Unlocked: ${unlockedChar}`);
                } else if (newLevel === LEARNING_ORDER.length + 1) {
                    setLevelUpMessage(`Congratulations! You've mastered all characters!`);
                }
                // Lazy load trivia data
                import('../data/trivia').then(({ getRandomTrivia }) => {
                    const newTrivia = getRandomTrivia();
                    setTrivia(newTrivia);
                    if (newTrivia) {
                        saveTriviaUnlock(newTrivia.id);
                        if (onUnlockTrivia) onUnlockTrivia(newTrivia.id);
                    }
                });
                setTimeout(() => {
                    setLevelUpMessage(null);
                    setTrivia(null);
                }, 15000); // Longer duration to read trivia

                // Reset history on level up so user has to prove proficiency at new level
                return {
                    ...prev,
                    level: newLevel,
                    maxLevel: newMaxLevel,
                    history: []
                };
            }
        }
        // Level Down (Regression): < 50% accuracy over last 10 attempts
        else if (newHistory.length >= 10 && accuracy < 0.5 && prev.level > 2) {
            newLevel = Math.max(2, prev.level - 1);
            if (newLevel < prev.level) {
                audio.playTone(0.5, 200, 'sawtooth'); // Sad sound
                setLevelUpMessage(`Level Down. Let's practice ${LEARNING_ORDER[newLevel - 1]} again.`);
                setTimeout(() => setLevelUpMessage(null), 3000);

                // Optional: Reset history on level down too? 
                // Usually good to give them a fresh start at the lower level.
                return {
                    ...prev,
                    level: newLevel,
                    maxLevel: newMaxLevel,
                    history: []
                };
            }
        }

        return {
            ...prev,
            level: newLevel,
            maxLevel: newMaxLevel,
            history: newHistory
        };
    };

    const setLevel = (level) => {
        setProgress(prev => ({
            ...prev,
            level: Math.min(level, prev.maxLevel || prev.level)
        }));
    };

    // ----- receive‑only text input handling -----
    const handleTextInput = useCallback((typedChar, isSubmit = false) => {
        if (!typedChar) return;

        if (gameMode === 'pileup') {
            if (isSubmit) {
                const upperInput = typedChar.toUpperCase().trim();

                // 1. Check for Partial Match or Replay via '?'
                if (upperInput.endsWith('?')) {
                    const rawQuery = upperInput.slice(0, -1);

                    // If just "?", replay all
                    if (rawQuery.length === 0) {
                        setInput('');
                        setIsAudioPlaying(true);
                        audio.playPileup(pileupTargets, wpm, farnsworthWpm).then(() => setIsAudioPlaying(false));
                        return;
                    }

                    // Find longest suffix of rawQuery that is a prefix of any target
                    let validPartial = '';
                    for (let i = 0; i < rawQuery.length; i++) {
                        const suffix = rawQuery.slice(i);
                        if (pileupTargets.some(t => t.startsWith(suffix))) {
                            validPartial = suffix;
                            break;
                        }
                    }

                    if (validPartial) {
                        const matches = pileupTargets.filter(call => call.startsWith(validPartial));
                        setInput('');
                        setLevelUpMessage(`Found ${matches.length} match${matches.length > 1 ? 'es' : ''}: ${matches.join(', ')}`);
                        setIsAudioPlaying(true);
                        audio.playPileup(matches, wpm, farnsworthWpm).then(() => setIsAudioPlaying(false));
                        setTimeout(() => setLevelUpMessage(null), 3000);
                    } else {
                        setInput('');
                        setLevelUpMessage(`No matches for "${rawQuery}"`);
                        setTimeout(() => setLevelUpMessage(null), 2000);
                    }
                    return;
                }

                // 2. Check for "AGN" (Again) command (allow trailing match)
                if (upperInput.endsWith('AGN')) {
                    setInput('');
                    setIsAudioPlaying(true);
                    audio.playPileup(pileupTargets, wpm, farnsworthWpm).then(() => setIsAudioPlaying(false));
                    return;
                }

                // 3. Check for Full Callsign Match (trailing)
                const matchedTarget = pileupTargets.find(t => upperInput.endsWith(t));

                if (matchedTarget) {
                    // Success
                    audio.playTone(0.1, 1000, 'sine');
                    setFeedback('success');
                    // setScore handled by checkProgress
                    setLevelUpMessage(`Caught ${matchedTarget}!`);

                    // Count as a callsign sequence for challenges
                    checkProgress('sequence', { mode: 'callsigns', amount: 1 });
                    checkProgress('score', { amount: 100, mode: 'pileup' });

                    setTimeout(async () => {
                        const targets = [];
                        for (let i = 0; i < pileupCount; i++) targets.push(generateCallSign());
                        setPileupTargets(targets);
                        setInput('');
                        setFeedback('neutral');
                        setLevelUpMessage(null);
                        setIsAudioPlaying(true);
                        await audio.playPileup(targets, wpm, farnsworthWpm);
                        setIsAudioPlaying(false);
                    }, 2000);
                } else {
                    // Error
                    audio.playError();
                    setFeedback('error');
                    setTimeout(() => setFeedback('neutral'), 1000);
                }
            } else {
                // Update input
                setInput(typedChar.toUpperCase());
            }
            return;
        }

        // Free-typing / Accumulating modes
        if (gameMode === 'practice' || gameMode === 'adventure' || gameMode === 'pro_qso' || gameMode === 'midnight_express') {
            const upperChar = typedChar.toUpperCase();
            if (typedChar === 'Backspace') {
                setInput(prev => prev.slice(0, -1));
            } else if (typedChar.length === 1 && /[A-Z0-9 ?.,/]/.test(upperChar)) {
                setInput(prev => prev + upperChar);
                setRecentChars(prev => [...prev.slice(-100), upperChar]);
            }
            return;
        }

        // Code Quiz Mode: Build up the answer
        if (gameMode === 'code_quiz') {
            if (feedback !== 'neutral') return;
            const upperChar = typedChar.toUpperCase();

            // Submission logic (Enter for keyboard, EE for Morse)
            const isMorseSubmit = quizInputMode === 'morse' && upperChar === 'E' && recentChars[recentChars.length - 1] === 'E';
            const isKeyboardSubmit = quizInputMode === 'keyboard' && (isSubmit || typedChar === 'Enter' || typedChar === '\n' || typedChar === '\r');

            if (isKeyboardSubmit || isMorseSubmit) {
                // Use immediate value for keyboard to avoid stale state issues
                let finalInput = quizInputMode === 'keyboard' ? upperChar : quizInput.toUpperCase();

                if (isMorseSubmit) {
                    // Remove the 'E' that was just added to quizInput as part of 'EE' trigger
                    finalInput = finalInput.slice(0, -1);
                }

                if (!finalInput.trim()) {
                    return; // Don't accept empty as an answer
                }

                // Check if the answer is correct
                if (finalInput.trim() === quizAnswer.toUpperCase()) {
                    // Correct!
                    setFeedback('success');
                    audio.playTone(0.1, 1000, 'sine');
                    // Bonus points for Morse!
                    const points = quizInputMode === 'morse' ? 100 : 50;
                    checkProgress('score', { amount: points, mode: gameMode });
                    checkProgress('count', { mode: gameMode, amount: 1, wpm });
                    updateSessionStats(true, quizAnswer);

                    // Play the correct answer in Morse code as reinforcement (requested)
                    setTimeout(() => {
                        setIsAudioPlaying(true);
                        audio.playText(quizAnswer, wpm, farnsworthWpm).then(() => {
                            setIsAudioPlaying(false);

                            // Move to next question after they've heard it
                            setTimeout(() => {
                                const quizItem = getNextTarget('code_quiz');
                                setQuizQuestion(quizItem.meaning);
                                setQuizAnswer(quizItem.code);
                                setQuizInput('');
                                setTarget('?');
                                setCurrentMeaning(quizItem.meaning);
                                setFeedback('neutral');
                            }, 1000);
                        });
                    }, 500);
                } else {
                    // Incorrect - Show the correct answer and teach it
                    setFeedback('error');
                    audio.playError();
                    checkProgress('accuracy', { isCorrect: false });
                    updateSessionStats(false, quizAnswer);

                    // Health Mechanic: Damage
                    setHealth(h => {
                        const newHealth = h - 15;
                        return newHealth;
                    });

                    // Show the correct answer
                    setQuizInput(quizAnswer);
                    setTarget(quizAnswer); // Display the correct code

                    // Play the correct answer in Morse code after a brief delay
                    setTimeout(() => {
                        setIsAudioPlaying(true);
                        audio.playText(quizAnswer, wpm, farnsworthWpm).then(() => {
                            setIsAudioPlaying(false);

                            // Check for game over
                            setHealth(currentHealth => {
                                if (currentHealth <= 0) {
                                    setView('gameover');
                                }
                                return currentHealth;
                            });

                            // Move to next question after they've heard the correct answer
                            setTimeout(() => {
                                const quizItem = getNextTarget('code_quiz');
                                setQuizQuestion(quizItem.meaning);
                                setQuizAnswer(quizItem.code);
                                setQuizInput('');
                                setTarget('?');
                                setCurrentMeaning(quizItem.meaning);
                                setFeedback('neutral');
                            }, 2000);
                        });
                    }, 1000);
                }
            } else if (feedback !== 'neutral') {
                return; // Block input during feedback
            } else if (typedChar === 'Backspace') {
                // Remove last character
                setQuizInput(prev => prev.slice(0, -1));
            } else if (quizInputMode === 'keyboard') {
                // In keyboard mode, we get the full string from the input field
                setQuizInput(upperChar);
            } else if (quizInputMode === 'morse' && typedChar.length === 1 && /[A-Z0-9]/.test(upperChar)) {
                // In Morse mode, we get single decoded characters
                setQuizInput(prev => prev + upperChar);
                // Update recent chars for EE detection
                setRecentChars(prev => [...prev.slice(-100), upperChar]);
            }
            return;
        }

        if (feedback !== 'neutral' || (inputMode !== 'keyboard' && !MORSE_MAP[typedChar?.toUpperCase()])) return;
        if (gameMode === 'qso' && currentQSO && currentQSO[qsoStep].speaker === 'them') return; // Block input during "Them" turn

        const upperChar = typedChar.toUpperCase();
        setRecentChars(prev => [...prev.slice(-100), upperChar]);

        // Experimental / View-managed modes handle their own scoring logic via recentChars
        if (gameMode === 'midnight_express' || gameMode === 'signal_lamp' || gameMode === 'constellation_hunter') {
            return;
        }

        if (upperChar === target) {
            setFeedback('success');
            checkProgress('score', { amount: 10, mode: gameMode });
            if (gameMode !== 'qso') {
                checkProgress('count', { mode: gameMode, amount: 1, wpm });
            }

            // Health Mechanic: Heal
            if (!IMMORTAL_MODES.includes(gameMode) && gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                setHealth(h => Math.min(100, h + 5));
            }

            checkProgress('accuracy', { isCorrect: true, farnsworthWpm, wpm });
            updateSessionStats(true, target, Date.now() - audioFinishTime.current);

            // Check for sequence completion immediately to avoid race conditions/unmounts
            if (gameMode === 'callsigns' || gameMode === 'activities' || gameMode === 'contests' || gameMode === 'qcodes' || gameMode === 'othercodes' || gameMode === 'icr_words' || gameMode === 'code_quiz' || (typeof gameMode === 'string' && gameMode.endsWith('_full'))) {
                let nextIdx = sequenceIndex + 1;
                if (currentSequence) {
                    while (nextIdx < currentSequence.length && currentSequence[nextIdx] === ' ') nextIdx++;
                }
                if (!currentSequence || nextIdx >= currentSequence.length) {
                    checkProgress('sequence', { mode: gameMode, amount: 1 });

                    // For code_quiz in Morse mode, move to next question
                    if (gameMode === 'code_quiz' && quizInputMode === 'morse') {
                        // Award bonus points for Morse input
                        checkProgress('score', { amount: 100, mode: gameMode });
                        setTimeout(() => {
                            const quizItem = getNextTarget('code_quiz');
                            setQuizQuestion(quizItem.meaning);
                            setQuizAnswer(quizItem.code);
                            setQuizInput('');
                            setCurrentMeaning(quizItem.meaning);
                            setCurrentSequence(quizItem.code);
                            setSequenceIndex(0);
                            setTarget(quizItem.code[0]);
                            setFeedback('neutral');
                        }, 1500);
                    }
                }
            }
        } else {
            setFeedback('error');
            audio.playError();
            checkProgress('accuracy', { isCorrect: false });
            updateSessionStats(false, target);

            // Health Mechanic: Damage
            if (!IMMORTAL_MODES.includes(gameMode) && gameMode !== 'icr_comprehension') {
                setHealth(h => h - 15);
            } else if (gameMode === 'icr_comprehension') {
                setLives((l) => l - 1);
            }

            setInput(upperChar);
            setTimeout(() => {
                setInput('');
                setFeedback('neutral');

                if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                    setHealth(currentHealth => {
                        if (currentHealth <= 0) {
                            setView('gameover');
                        }
                        return currentHealth;
                    });
                } else if (gameMode === 'icr_comprehension') {
                    if (lives <= 1) setView('gameover');
                }
            }, 1000);
        }
    }, [feedback, inputMode, target, gameMode, sequenceIndex, currentSequence, callSignRegion, activityType, activityRole, contestType, progress, playbackMode, wpm, farnsworthWpm, audio, getNextTarget, userSettings, lives, pileupTargets, input, recentChars, quizInput, quizAnswer, quizInputMode]);

    // Handle sequence advancement after success feedback
    useEffect(() => {
        if (feedback === 'success') {

            if (gameMode === 'icr_comprehension') return;

            // Bypass delay if Instant Text is ON (allow rapid typing)
            const delay = instantText ? 0 : (gameMode === 'guided' ? 1500 : 500);

            const timer = setTimeout(() => {
                if (gameMode === 'constellation_hunter') {
                    setInput('');
                    setFeedback('neutral');
                    return;
                }

                try {
                    let nextChar = '';
                    // 1. SEQUENCE MODES (Callsigns, Activities, QSO, etc)
                    if (gameMode === 'callsigns' || gameMode === 'activities' || gameMode === 'contests' || gameMode === 'qcodes' || gameMode === 'othercodes' || gameMode === 'icr_words' || gameMode === 'qso' || (typeof gameMode === 'string' && gameMode.endsWith('_full'))) {

                        // Advance within current sequence?
                        let nextIdx = sequenceIndex + 1;
                        if (currentSequence) {
                            // Skip spaces and characters without Morse code mappings
                            while (nextIdx < currentSequence.length && (currentSequence[nextIdx] === ' ' || !MORSE_MAP[currentSequence[nextIdx]])) {
                                nextIdx++;
                            }
                        }

                        if (currentSequence && nextIdx < currentSequence.length) {
                            // Still in sequence
                            setSequenceIndex(nextIdx);
                            nextChar = currentSequence[nextIdx];
                            setTarget(nextChar);
                        } else {
                            // Sequence Finished -> Get NEW Sequence
                            let newSeq = '';

                            if (gameMode === 'callsigns' || gameMode === 'callsigns_full') newSeq = getNextTarget('callsigns', null, callSignRegion);
                            else if (gameMode === 'qcodes_full') newSeq = getNextTarget('qcodes');
                            else if (gameMode === 'othercodes_full') newSeq = getNextTarget('othercodes');
                            else if (gameMode === 'activities') newSeq = getNextTarget('activities', null, null, activityType, activityRole);
                            else if (gameMode === 'contests') newSeq = getNextTarget('contests', null, null, contestType);
                            else if (gameMode === 'icr_words') newSeq = getNextTarget('icr_words');
                            else if (gameMode === 'qso') {
                                const nextStep = qsoStep + 1;
                                if (nextStep < currentQSO.length) {
                                    // Set the next turn's text as the new sequence
                                    const nextTurn = currentQSO[nextStep];
                                    newSeq = nextTurn.text;

                                    // CRITICAL: Advance the QSO step!
                                    setQsoStep(nextStep);

                                    console.log('[QSO] Advancing to next step in feedback effect:', {
                                        currentStep: qsoStep,
                                        nextStep,
                                        nextSpeaker: nextTurn.speaker,
                                        nextText: nextTurn.text
                                    });
                                } else {
                                    if (isQsoCompleting.current) return;
                                    isQsoCompleting.current = true;

                                    setFeedback('neutral');
                                    setInput('');
                                    setLevelUpMessage("QSO Complete! 73!");
                                    checkProgress('qso_complete');
                                    checkProgress('score', { amount: 500, mode: 'qso' });
                                    if (currentQSO.station && currentQSO.station.location) {
                                        checkProgress('country', { country: currentQSO.station.location.country });
                                    }
                                    if (onChallengeUpdate) {
                                        onChallengeUpdate({ type: 'sequence', amount: 1, mode: 'qso' });
                                    }
                                    audio.playLevelUp();
                                    setTimeout(() => setView('menu'), 3000);
                                    return;
                                }
                            }
                            else newSeq = getNextTarget(gameMode);

                            setCurrentSequence(newSeq);
                            updateMeaning(gameMode, newSeq);

                            // Setup Target for New Sequence
                            if (gameMode === 'qso' && currentQSO && currentQSO[qsoStep + 1]?.speaker === 'them') {
                                setTarget(null);
                                setSequenceIndex(0);
                                nextChar = '';
                            } else {
                                let firstIdx = 0;
                                // Skip spaces and characters without Morse code mappings
                                while (firstIdx < newSeq.length && (newSeq[firstIdx] === ' ' || !MORSE_MAP[newSeq[firstIdx]])) {
                                    firstIdx++;
                                }
                                setSequenceIndex(firstIdx);
                                nextChar = newSeq[firstIdx];
                                setTarget(nextChar);

                                setScore((s) => s + 50);
                                audio.playLevelUp();
                            }

                            // Special: ICR / Full Sequence playback (ONLY if NOT instantText)
                            if ((gameMode === 'icr_words' || (typeof gameMode === 'string' && gameMode.endsWith('_full'))) && !instantText && !isMuteOutgoing) {
                                setTimeout(() => {
                                    setIsAudioPlaying(true);
                                    setPlaybackIndex(0);
                                    audio.playText(newSeq, wpm, farnsworthWpm, (idx) => {
                                        setPlaybackIndex(idx);
                                    }).then(() => {
                                        setIsAudioPlaying(false);
                                        setPlaybackIndex(-1);
                                        audioFinishTime.current = Date.now();
                                    });
                                }, 500);
                            }
                        }
                    } else {
                        // 2. SINGLE CHAR MODES (Guided, Copy, etc)
                        nextChar = getNextTarget(gameMode, progress, callSignRegion, activityType, activityRole);
                        setTarget(nextChar);
                    }

                    // Common cleanup after update
                    setInput('');
                    setFeedback('neutral');

                    // Audio Playback for next char (if needed)
                    if (playbackMode === 'char' && gameMode !== 'icr_words' && nextChar && !instantText && !isMuteOutgoing) {
                        const code = MORSE_MAP[nextChar];
                        if (code) {
                            setIsAudioPlaying(true);
                            audio.playSequence(code, wpm, farnsworthWpm).then(() => {
                                setIsAudioPlaying(false);
                                audioFinishTime.current = Date.now();
                            });
                        }
                    }

                    if (gameMode === 'guided') {
                        setProgress((prev) => {
                            const next = calculateProgress(prev, true);
                            const historyThatTriggered = [...prev.history, true].slice(-20);
                            const acc = (historyThatTriggered.filter(Boolean).length / historyThatTriggered.length) * 100;

                            if (next.level > prev.level || (next.level >= 40 && historyThatTriggered.length >= 10 && historyThatTriggered.length % 10 === 0)) {
                                // Trigger achievement check for level completion
                                setTimeout(() => {
                                    checkProgress('level_complete', {
                                        level: next.level,
                                        inputMode,
                                        accuracy: acc,
                                        wpm,
                                        farnsworthWpm
                                    });
                                }, 100);
                            }
                            return next;
                        });
                    }

                } catch (err) {
                    console.error('Error in advancement effect:', err);
                    setFeedback('neutral');
                    setInput('');
                }
            }, delay);
            return () => clearTimeout(timer);
        }
    }, [feedback, gameMode, sequenceIndex, currentSequence, callSignRegion, activityType, activityRole, contestType, qsoStep, currentQSO, progress, playbackMode, wpm, farnsworthWpm, getNextTarget, audio, checkProgress, instantText, isMuteOutgoing]);

    const [currentSignal, setCurrentSignal] = useState('');

    // ----- paddle / mic signal registration -----
    const registerSignal = (type) => {
        if (feedback === 'error') return;

        // Clear any pending decoding/strict timeout when a new signal arrives
        if (decodingTimeout.current) clearTimeout(decodingTimeout.current);

        // If we already had a success and another signal comes in before the target advanced,
        // it means the user overshot (sent too many signals for the current character).
        if (feedback === 'success' && gameMode !== 'practice' && gameMode !== 'pileup' && gameMode !== 'adventure' && gameMode !== 'pro_qso' && gameMode !== 'midnight_express') {
            audio.playError();
            setFeedback('error');
            checkProgress('accuracy', { isCorrect: false });
            updateSessionStats(false, target);
            if (!IMMORTAL_MODES.includes(gameMode) && gameMode !== 'icr_comprehension') {
                setHealth(h => h - 15);
            } else if (gameMode === 'icr_comprehension') {
                setLives(l => l - 1);
            }

            // Reset after error feedback delay
            setTimeout(() => {
                setInput('');
                setCurrentSignal('');
                setFeedback('neutral');

                // Check for game over
                if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                    setHealth(currentHealth => {
                        if (currentHealth <= 0) {
                            setView('gameover');
                        }
                        return currentHealth;
                    });
                } else if (gameMode === 'icr_comprehension') {
                    if (lives <= 1) setView('gameover');
                }
            }, 1000);

            return;
        }

        if (feedback !== 'neutral' && gameMode !== 'fist_doctor' && gameMode !== 'iambic_squeeze') return;

        // In Lab modes, clear feedback as soon as they start a new character signal
        if (currentSignal === '' && (gameMode === 'fist_doctor' || gameMode === 'iambic_squeeze')) {
            setFeedback('neutral');
        }

        if (gameMode === 'qso' && currentQSO && currentQSO[qsoStep].speaker === 'them') return; // Block input during "Them" turn

        const char = type === 'dot' ? '.' : '-';
        const newSignal = currentSignal + char;
        setCurrentSignal(newSignal);

        // In standard modes, 'input' is the signal buffer. In Pileup, 'input' is the text.
        // We only update 'input' with signals if NOT in pileup mode.
        let newInput = input;
        if (gameMode !== 'pileup' && gameMode !== 'pro_qso') {
            newInput = input + char;
            setInput(newInput);
        }

        // IMMEDIATE VALIDATION: Check if input is too long (user holding paddle too long)
        // This catches the error RIGHT AWAY instead of waiting for character gap timeout
        if (gameMode !== 'practice' && gameMode !== 'pileup' && gameMode !== 'pro_qso' && gameMode !== 'midnight_express' && target) {
            const correctCode = MORSE_MAP[target];
            if (correctCode && newInput.length > correctCode.length) {
                // Input is TOO LONG - user sent too many dits/dahs
                console.log('[Input Too Long] Immediate catch:', {
                    target,
                    expected: correctCode,
                    actual: newInput,
                    expectedLength: correctCode.length,
                    actualLength: newInput.length
                });

                audio.playError();
                setFeedback('error');
                checkProgress('accuracy', { isCorrect: false });
                updateSessionStats(false, target);

                // Health Mechanic: Damage
                if (!IMMORTAL_MODES.includes(gameMode) && gameMode !== 'icr_comprehension') {
                    setHealth(h => h - 15);
                } else if (gameMode === 'icr_comprehension') {
                    setLives((l) => l - 1);
                }

                // Reset after error
                setTimeout(() => {
                    setInput('');
                    setCurrentSignal('');
                    setFeedback('neutral');

                    if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                        setHealth(currentHealth => {
                            if (currentHealth <= 0) {
                                setView('gameover');
                            }
                            return currentHealth;
                        });
                    } else if (gameMode === 'icr_comprehension') {
                        if (lives <= 1) setView('gameover');
                    }
                }, 1000);

                return; // Stop processing this input
            }
        }

        if (gameMode === 'practice' || gameMode === 'pileup' || gameMode === 'adventure' || gameMode === 'telegraph_hangman' || gameMode === 'code_quiz' || gameMode === 'fist_doctor' || gameMode === 'iambic_squeeze' || gameMode === 'pro_qso' || gameMode === 'midnight_express' || gameMode === 'signal_word') {
            if (decodingTimeout.current) clearTimeout(decodingTimeout.current);
            // Use Farnsworth speed for spacing (gaps) to allow for easier timing
            const spacingWpm = farnsworthWpm || wpm;
            const unitMs = (1.2 / spacingWpm) * 1000;
            // Wait for character gap (Standard is 3 units. Using 6 units for easier manual keying)
            decodingTimeout.current = setTimeout(() => {
                // Check for Error Signal (8 dots) -> Clear Line
                if (newSignal === '........') {
                    if (gameMode === 'practice' || gameMode === 'adventure') {
                        setPracticeText('');
                        audio.playTone(0.3, 150, 'sawtooth'); // distinctive "erasure" sound
                    }
                    // Reset signal buffer
                    setCurrentSignal('');
                    if (gameMode !== 'pileup') setInput('');
                    return;
                }

                const foundChar = Object.keys(MORSE_MAP).find(key => MORSE_MAP[key] === newSignal);
                if (foundChar) {
                    if (gameMode === 'practice' || gameMode === 'adventure' || gameMode === 'signal_word') {
                        setPracticeText(prev => prev + foundChar);
                    } else if (gameMode === 'code_quiz') {
                        handleTextInput(foundChar);
                    } else if (gameMode === 'fist_doctor' || gameMode === 'iambic_squeeze') {
                        // In Lab modes, if they hit the target, trigger success
                        if (foundChar === target) {
                            setFeedback('success');
                            setScore(s => s + 50);
                            checkProgress('score', { amount: 50, mode: gameMode });
                            checkProgress('score_update', { score: score + 50 });
                            checkProgress('count', { mode: gameMode, amount: 1, wpm });
                            checkProgress('accuracy', { isCorrect: true, farnsworthWpm, wpm });
                            updateSessionStats(true, target);
                        } else {
                            setFeedback('error');
                            audio.playError();
                            checkProgress('accuracy', { isCorrect: false });
                            updateSessionStats(false, target);
                            // Reset feedback after a delay so they can try again
                            setTimeout(() => {
                                setFeedback('neutral');
                                setInput('');
                                setCurrentSignal('');
                            }, 500);
                        }
                    } else if (gameMode !== 'telegraph_hangman' && gameMode !== 'fist_doctor' && gameMode !== 'iambic_squeeze') {
                        // Pileup mode or Pro QSO: append to text input
                        setInput(prev => prev + foundChar);
                    }
                    // Update recentChars for achievements and experimental modes
                    setRecentChars(prev => [...prev.slice(-100), foundChar]);
                }

                // Clear signal buffer
                setCurrentSignal('');
                if (gameMode !== 'pileup' && gameMode !== 'pro_qso') {
                    setInput('');
                }

                if (gameMode === 'practice' || gameMode === 'adventure' || gameMode === 'pro_qso' || gameMode === 'midnight_express' || gameMode === 'signal_word') {
                    // Wait for word gap (Standard is 7 units. Using 10 units total = 6 char gap + 4 more)
                    decodingTimeout.current = setTimeout(() => {
                        const spacer = (gameMode === 'pro_qso' || gameMode === 'pileup') ? ' ' : ' ';
                        if (gameMode === 'pro_qso' || gameMode === 'midnight_express') {
                            setInput(prev => prev.endsWith(' ') ? prev : prev + ' ');
                            setRecentChars(prev => [...prev.slice(-100), ' ']);
                        } else {
                            setPracticeText(prev => prev + ' ');
                            setRecentChars(prev => [...prev.slice(-100), ' ']);
                        }
                    }, unitMs * (gameMode === 'midnight_express' ? 5 : (gameMode === 'pro_qso' ? 15 : 4)));
                }
            }, unitMs * (gameMode === 'midnight_express' ? 3.5 : 6));
            return;
        }

        const correctCode = target ? MORSE_MAP[target] : null;
        if (!correctCode) return;
        if (newInput === correctCode) {
            // Update recentChars for achievements
            setRecentChars(prev => [...prev.slice(-100), target]);
            setFeedback('success');
            checkProgress('score', { amount: 10, mode: gameMode });
            if (gameMode !== 'qso') {
                checkProgress('count', { mode: gameMode, amount: 1, wpm });
            }
            checkProgress('char_sent', { inputMode });
            checkProgress('accuracy', { isCorrect: true });
            updateSessionStats(true, target, Date.now() - audioFinishTime.current);

            // Health Mechanic: Heal
            if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                setHealth(h => Math.min(100, h + 5));
            }

            // Check for sequence completion immediately
            if (gameMode === 'callsigns' || gameMode === 'activities' || gameMode === 'contests' || gameMode === 'qcodes' || gameMode === 'othercodes' || gameMode === 'icr_words') {
                let nextIdx = sequenceIndex + 1;
                if (currentSequence) {
                    while (nextIdx < currentSequence.length && currentSequence[nextIdx] === ' ') nextIdx++;
                }
                if (!currentSequence || nextIdx >= currentSequence.length) {
                    checkProgress('sequence', { mode: gameMode, amount: 1 });
                }
            }

            setInput('');
            setCurrentSignal('');
            // Logic moved to useEffect below to prevent double-advancement
        } else if (newInput.length > correctCode.length) {
            // Input is too long - user sent too many dits/dahs
            audio.playError();
            setFeedback('error');
            checkProgress('accuracy', { isCorrect: false });
            checkProgress('accuracy', { isCorrect: false });
            updateSessionStats(false, target);

            // Health Mechanic: Damage
            if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                setHealth(h => {
                    const newHealth = h - 15;
                    return newHealth;
                });
            } else if (gameMode === 'icr_comprehension') {
                setLives((l) => l - 1);
            }

            if (gameMode === 'guided') {
                setProgress((p) => calculateProgress(p, false));
            }
            setTimeout(() => {
                setInput('');
                setCurrentSignal('');
                setFeedback('neutral');

                if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                    setHealth(currentHealth => {
                        if (currentHealth <= 0) {
                            setView('gameover');
                        }
                        return currentHealth;
                    });
                } else if (gameMode === 'icr_comprehension') {
                    if (lives <= 1) setView('gameover');
                }
            }, 500);
        } else if (!correctCode.startsWith(newInput)) {
            // wrong sequence (doesn't match expected prefix)
            audio.playError();
            setFeedback('error');
            checkProgress('accuracy', { isCorrect: false });
            checkProgress('accuracy', { isCorrect: false });
            updateSessionStats(false, target);

            // Health Mechanic: Damage
            if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                setHealth(h => {
                    const newHealth = h - 15;
                    return newHealth;
                });
            } else if (gameMode === 'icr_comprehension') {
                setLives((l) => l - 1);
            }

            if (gameMode === 'guided') {
                setProgress((p) => calculateProgress(p, false));
            }
            setTimeout(() => {
                setInput('');
                setCurrentSignal('');
                setFeedback('neutral');

                if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                    setHealth(currentHealth => {
                        if (currentHealth <= 0) {
                            setView('gameover');
                        }
                        return currentHealth;
                    });
                } else if (gameMode === 'icr_comprehension') {
                    if (lives <= 1) setView('gameover');
                }
            }, 500);
        } else {

            // Strict Timing: If enabled, enforce max pause between signals
            // Default to ON for Mastery (QSO) and ICR modes, unless explicitly disabled in settings
            const strictDefault = ['qso', 'icr_words', 'icr_echo', 'icr_comprehension'].includes(gameMode);
            const enforceTiming = userSettings?.strictTiming !== undefined ? userSettings.strictTiming : strictDefault;

            if (enforceTiming && (gameMode !== 'practice' && gameMode !== 'pileup')) {
                // Use Farnsworth speed for strict timing window (Increased to 5 units to prevent race condition with Iambic dash)
                const spacingWpm = farnsworthWpm || wpm;
                const unitMs = (1.2 / spacingWpm) * 1000;

                decodingTimeout.current = setTimeout(() => {
                    // Check if user is actively holding a paddle key (Iambic squeeze or hold)
                    if (paddleState.current.left || paddleState.current.right) return;

                    // Timeout fired! User waited too long with an incomplete signal.
                    // We know it's incomplete because if it was complete, we would have hit the 'Success' block above.

                    console.log('[Strict Timing] Timeout: Incomplete character', { actual: newInput, expected: correctCode });

                    audio.playError();
                    setFeedback('error');
                    checkProgress('accuracy', { isCorrect: false });
                    updateSessionStats(false, target);

                    // Health Damage
                    if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                        setHealth(h => h - 15);
                    } else if (gameMode === 'icr_comprehension') {
                        setLives(l => l - 1);
                    }

                    // Reset
                    setTimeout(() => {
                        setInput('');
                        setCurrentSignal('');
                        setFeedback('neutral');

                        if (gameMode !== 'adventure' && gameMode !== 'icr_comprehension') {
                            setHealth(currentHealth => {
                                if (currentHealth <= 0) setView('gameover');
                                return currentHealth;
                            });
                        } else if (gameMode === 'icr_comprehension') {
                            if (lives <= 1) setView('gameover');
                        }
                    }, 1000);
                }, unitMs * 5);
            }
        }
    };

    // Save Activity Log on session end (View Change)


    // Save on Unmount (Refresh/Close)
    useEffect(() => {
        return () => {
            saveSession('Unmount');
        };
    }, [saveSession]);



    // ----- Paddle / Keyer Logic (Iambic) -----
    const keyerActive = useRef(false);
    const lastElement = useRef(null); // 'dot' or 'dash'
    const processKeyerRef = useRef(null);

    processKeyerRef.current = () => {
        if (!paddleState.current.left && !paddleState.current.right) {
            keyerActive.current = false;
            return;
        }

        let type = null;

        // Iambic B Logic (Alternate if both pressed)
        if (paddleState.current.left && paddleState.current.right) {
            if (lastElement.current === 'dot') type = 'dash';
            else type = 'dot';
        } else if (paddleState.current.left) {
            type = 'dot';
        } else if (paddleState.current.right) {
            type = 'dash';
        }

        if (type) {
            lastElement.current = type;

            // Calculate timing
            const unit = 1.2 / wpm;
            const duration = type === 'dot' ? unit : unit * 3;

            // Play sound
            audio.playTone(duration);
            vibrate(10);

            // Register signal
            registerSignal(type);
            if (onElementEvent) {
                onElementEvent(type, duration * 1000);
            }

            // Schedule next element (duration + 1 unit gap)
            setTimeout(() => {
                if (processKeyerRef.current) processKeyerRef.current();
            }, (duration + unit) * 1000);
        } else {
            keyerActive.current = false;
        }
    };

    const startKeyer = () => {
        if (keyerActive.current) return;
        keyerActive.current = true;
        if (processKeyerRef.current) processKeyerRef.current();
    };

    // Safety Effect: Stop audio/keyer loops when leaving game view
    useEffect(() => {
        if (view === 'gameover' || view === 'menu') {
            audio.stopPlayback();
            // Reset Input State to prevent infinite keyer loops
            if (paddleState.current) paddleState.current = { left: false, right: false };
            if (keyerActive.current) keyerActive.current = false;
        }
    }, [view, audio]);


    const handlePaddle = (type) => {
        // Legacy/Manual trigger (single shot)
        const unit = 1.2 / wpm;
        const duration = type === 'dot' ? unit : unit * 3;
        if (type === 'dot') audio.playTone(unit);
        else audio.playTone(unit * 3);
        vibrate(10);
        setTimeout(() => {
            registerSignal(type);
            if (onElementEvent) onElementEvent(type, duration * 1000);
        }, 0);
    };

    const handleStraightDown = (e) => {
        if (e) e.preventDefault();
        if (isKeyPressed.current || feedback !== 'neutral') return;
        isKeyPressed.current = true;
        keyPressStart.current = Date.now();
        audio.startTone();
        vibrate(15);
        if (onMove) onMove('straight', true);
    };

    const handleStraightUp = (e) => {
        if (e) e.preventDefault();
        if (!isKeyPressed.current) return;
        isKeyPressed.current = false;
        audio.stopTone();
        if (onMove) onMove('straight', false);
        const duration = Date.now() - keyPressStart.current;
        const unitMs = (1.2 / wpm) * 1000;
        const threshold = Math.max(150, unitMs * 2);
        const type = duration < threshold ? 'dot' : 'dash';
        registerSignal(type);
        if (onElementEvent) onElementEvent(type, duration);
    };


    // ----- global key listeners (except receive‑only) -----
    useEffect(() => {
        const handleKeyDown = (e) => {
            const isInputView = (view === 'game' || view.startsWith('game/')) || view === 'adventure' || view === 'telegraph_hangman' || view === 'proqso' || view === 'lab' || view === 'midnight_express' || view === 'fist_doctor' || view === 'iambic_squeeze' || view === 'constellation_hunter' || view === 'signal_word';
            if (!isInputView) return;

            // Ignore if typing in an editable input field
            const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
            if (isInput && !e.target.readOnly) return;

            // Practice Mode: Spacebar clears text
            if (gameMode === 'practice' && e.key === ' ' && inputMode !== 'straight') {
                e.preventDefault();
                setPracticeText('');
                return;
            }
            if (e.repeat) return; // Ignore repeats, we handle looping manually

            // Replay Hotkey: Backspace triggers audio replay
            // In keyboard mode, only when input is empty (so it doesn't interfere with deleting text)
            // Excluded from ProQSO (uses Backspace to clear TX area)
            if (e.key === 'Backspace' && view !== 'proqso' && !isAudioPlaying && (inputMode !== 'keyboard' || !input)) {
                e.preventDefault();
                handlePlayback();
                return;
            }

            if (inputMode === 'keyboard') return;

            const isReversed = userSettings.paddleReversed;

            // Determine which keys map to Dot (Left) and Dash (Right)
            // We check both e.code (new robust way) and e.key (legacy/default way)
            // Default: Dot = KeyZ/z/., Dash = KeyX/x//

            const isDotKey = (e) => e.code === userSettings.keyMap?.dot || e.key === userSettings.keyMap?.dot || e.key === '.' || e.key === 'z' || e.code === 'KeyZ';
            const isDashKey = (e) => e.code === userSettings.keyMap?.dash || e.key === userSettings.keyMap?.dash || e.key === '/' || e.key === 'x' || e.code === 'KeyX';

            const triggersDot = isReversed ? isDashKey(e) : isDotKey(e);
            const triggersDash = isReversed ? isDotKey(e) : isDashKey(e);

            if (inputMode === 'bug') {
                if (triggersDot) {
                    paddleState.current.left = true;
                    if (onMove) onMove('left', true);
                    startKeyer();
                }
                if (triggersDash) {
                    handleStraightDown();
                    // handleStraightDown should handle its own analytics if needed, 
                    // but for the analyticEngine, 'straight' is usually a separate flow?
                    // Actually, analyticEngine.recordMove is generic.
                    if (onMove) onMove('straight', true);
                }
            } else if (inputMode === 'paddle') {
                if (triggersDot) {
                    paddleState.current.left = true;
                    if (onMove) onMove('left', true);
                    startKeyer();
                }
                if (triggersDash) {
                    paddleState.current.right = true;
                    if (onMove) onMove('right', true);
                    startKeyer();
                }
            } else {
                if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    handleStraightDown();
                }

                // Support simulated paddles (Z/X) even in straight mode for specific games
                if (view === 'constellation_hunter' || view === 'midnight_express' || view === 'signal_word') {
                    if (triggersDot) {
                        paddleState.current.left = true;
                        if (onMove) onMove('left', true);
                        startKeyer();
                    }
                    if (triggersDash) {
                        paddleState.current.right = true;
                        if (onMove) onMove('right', true);
                        startKeyer();
                    }
                }
            }
        };

        const handleKeyUp = (e) => {
            const isInputView = (view === 'game' || view.startsWith('game/')) || view === 'adventure' || view === 'telegraph_hangman' || view === 'proqso' || view === 'lab' || view === 'midnight_express' || view === 'fist_doctor' || view === 'iambic_squeeze' || view === 'constellation_hunter' || view === 'signal_word';
            if (!isInputView) return;
            if (inputMode === 'keyboard') return;

            const isReversed = userSettings.paddleReversed;
            const isDotKey = (e) => e.code === userSettings.keyMap?.dot || e.key === userSettings.keyMap?.dot || e.key === '.' || e.key === 'z' || e.code === 'KeyZ';
            const isDashKey = (e) => e.code === userSettings.keyMap?.dash || e.key === userSettings.keyMap?.dash || e.key === '/' || e.key === 'x' || e.code === 'KeyX';

            const triggersDot = isReversed ? isDashKey(e) : isDotKey(e);
            const triggersDash = isReversed ? isDotKey(e) : isDashKey(e);

            if (inputMode === 'bug') {
                if (triggersDot) {
                    paddleState.current.left = false;
                    if (onMove) onMove('left', false);
                }
                if (triggersDash) {
                    handleStraightUp();
                    if (onMove) onMove('straight', false);
                }
            } else if (inputMode === 'paddle') {
                if (triggersDot) {
                    paddleState.current.left = false;
                    if (onMove) onMove('left', false);
                }
                if (triggersDash) {
                    paddleState.current.right = false;
                    if (onMove) onMove('right', false);
                }
            } else if (inputMode === 'straight' || view === 'constellation_hunter' || view === 'midnight_express') {
                if (e.key === ' ' || e.key === 'Enter') {
                    handleStraightUp();
                }

                // Release simulated paddles
                if (view === 'constellation_hunter' || view === 'midnight_express' || view === 'signal_word') {
                    if (triggersDot) {
                        paddleState.current.left = false;
                        if (onMove) onMove('left', false);
                    }
                    if (triggersDash) {
                        paddleState.current.right = false;
                        if (onMove) onMove('right', false);
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [
        view,
        inputMode,
        input,
        target,
        feedback,
        lives,
        wpm,
        gameMode,
        progress,
        currentSequence,
        sequenceIndex,
        callSignRegion,
        activityType,
        activityRole,
        contestType,
        userSettings,
        handlePaddle,
        handleStraightDown,
        handleStraightUp,
        isAudioPlaying,
        handlePlayback,
    ]);

    // ----- MIDI Input Handling -----
    const { activeNotes, isMidiAvailable } = useMidiInput();
    // ----- Gamepad Input Handling -----
    const { activeButtons: gamepadButtons, isGamepadConnected } = useGamepadInput(userSettings.gamepadMap);

    useEffect(() => {
        // --- MIDI Logic ---
        if (isMidiAvailable) {
            // Straight Key (Note 0)
            if (activeNotes[0]) {
                handleStraightDown();
            } else if (!activeNotes[0] && isKeyPressed.current) {
                handleStraightUp();
            }
        }

        // --- Combined Paddle Logic (MIDI + Gamepad) ---
        // We combine inputs so either can trigger the paddle
        // Note 1 / Button Dot -> Usually Left (Dot)
        // Note 2 / Button Dash -> Usually Right (Dash)

        const inputDot = (isMidiAvailable && activeNotes[1]) || (isGamepadConnected && gamepadButtons.dot);
        const inputDash = (isMidiAvailable && activeNotes[2]) || (isGamepadConnected && gamepadButtons.dash);

        // Apply Reverse Paddle Logic
        const isReversed = userSettings.paddleReversed;
        const triggerDot = isReversed ? inputDash : inputDot;
        const triggerDash = isReversed ? inputDot : inputDash;

        // Paddle Dot
        if (triggerDot) {
            paddleState.current.left = true;
            startKeyer();
        } else {
            paddleState.current.left = false;
        }

        // Paddle Dash
        if (triggerDash) {
            paddleState.current.right = true;
            startKeyer();
        } else {
            paddleState.current.right = false;
        }
    }, [activeNotes, isMidiAvailable, gamepadButtons, isGamepadConnected, userSettings.paddleReversed]);

    // ----- expose hook API -----
    const handleComprehensionAnswer = (selectedOption) => {
        if (!comprehensionItem) return;

        const isCorrect = selectedOption?.toString().trim().toUpperCase() === comprehensionItem.answer?.toString().trim().toUpperCase();
        updateSessionStats(isCorrect, null);

        if (isCorrect) {
            setFeedback('success');
            // setScore handled by checkProgress
            audio.playTone(0.1, 1000, 'sine');
            // setLevelUpMessage("Correct!"); // Redundant with GameView display
            setLevelUpMessage(null); // Clear any previous message
            checkProgress('head_copy_session');
            checkProgress('count', { mode: 'icr_comprehension', amount: 1 });
            checkProgress('score', { amount: 100, mode: 'icr_comprehension' });

            // Track head copy words for timeline unlock
            const wordCount = (comprehensionItem.originalText || comprehensionItem.text).split(/\s+/).filter(w => w.length > 0).length;
            setUserSettings(prev => {
                const updated = { ...prev, headCopyWords: (prev.headCopyWords || 0) + wordCount };
                localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
                return updated;
            });
        } else {
            setFeedback('error');
            audio.playError();
            // setLevelUpMessage(`Wrong! Answer: ${comprehensionItem.answer}`); // Confusing UI (Green bubble)
            setLevelUpMessage(null); // Clear any previous message
        }

        const isLiterature = comprehensionMode === 'literature' || comprehensionMode === 'stories' || comprehensionMode === 'poems';

        // Logic for multi-question segments
        if (isLiterature && comprehensionItem.questions && currentSegmentQuestionIndex < comprehensionItem.questions.length - 1) {
            const nextQIndex = currentSegmentQuestionIndex + 1;
            const nextQ = comprehensionItem.questions[nextQIndex];

            setTimeout(() => {
                setCurrentSegmentQuestionIndex(nextQIndex);
                setComprehensionItem(prev => ({
                    ...prev,
                    question: nextQ.question,
                    options: nextQ.options,
                    answer: nextQ.answer
                }));
                setComprehensionState('answering');
                setFeedback('neutral');
            }, 1500);
        } else if (isLiterature && currentLiteratureId) {
            // Check if there's a next segment
            const segments = getSegments(currentLiteratureId);
            if (currentSegmentIndex < segments.length - 1) {
                // We'll show the result state, but the "Next Drill" button should advance segment
                setComprehensionState('result');
            } else {
                setComprehensionState('result');
            }
        } else {
            setComprehensionState('result');
        }
    };

    // Helper to advance segment
    const advanceLiteratureSegment = () => {
        const nextSegmentIndex = currentSegmentIndex + 1;
        const segments = getSegments(currentLiteratureId);

        if (nextSegmentIndex < segments.length) {
            setCurrentSegmentIndex(nextSegmentIndex);
            setCurrentSegmentQuestionIndex(0); // Reset to first question of new segment
            // Pass nextSegmentIndex explicitly to avoid async state issues
            startGame('icr_comprehension', false, currentLiteratureId, nextSegmentIndex);
        } else {
            // All segments complete - track completion
            if (currentLiteratureId) {
                trackLiteratureProgress(currentLiteratureId, 'completed');
            }
            setView('literature_library'); // Return to library
        }
    };

    // Helper to track literature progress
    const trackLiteratureProgress = (id, status) => {
        if (!id || !setUserSettings) return;

        let syncData = null;

        setUserSettings(prev => {
            const history = prev.literatureHistory || {};
            const item = history[id] || { id, status: 'none', completedSegments: 0, lastAccessed: null };

            // Don't downgrade status (completed > started > none)
            let newStatus = status;
            if (item.status === 'completed') newStatus = 'completed';
            else if (item.status === 'started' && status === 'none') newStatus = 'started';

            const newCompletedSegments = Math.max(item.completedSegments || 0, (status === 'completed' ? 999 : currentSegmentIndex));

            history[id] = {
                ...item,
                status: newStatus,
                lastAccessed: new Date().toISOString(),
                completedSegments: newCompletedSegments
            };

            syncData = { id, status: newStatus, completedSegments: newCompletedSegments };

            const updated = { ...prev, literatureHistory: history };
            localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
            return updated;
        });

        // Save to database outside of state update
        if (syncData) {
            saveLiteratureProgress(syncData.id, syncData.status, syncData.completedSegments);
        }
    };
    return {
        view,
        setView,
        gameMode,
        setGameMode,
        inputMode,
        setInputMode,
        playbackMode,
        setPlaybackMode,
        playbackIndex,
        wpm,
        setWpm,
        farnsworthWpm,
        setFarnsworthWpm,
        noiseLevel,
        setNoiseLevel,
        qsbLevel,
        setQsbLevel,
        callSignRegion,
        setCallSignRegion,
        activityType,
        setActivityType,
        activityRole,
        setActivityRole,
        contestType,
        setContestType,
        qsoRole,
        setQsoRole,
        currentQSO,
        qsoStep,
        score,
        lives,
        setLives,
        health,
        setHealth,
        target,
        input,
        setInput,
        feedback,
        levelUpMessage,
        trivia,
        currentSequence,
        setCurrentSequence,
        currentMeaning,
        sequenceIndex,
        isAudioPlaying,
        isMuteOutgoing,
        setIsMuteOutgoing: setIsMuteOutgoingWithSave,
        progress,
        setProgress,
        setLevel,
        startGame,
        handlePlayback,
        handlePaddle,
        handleStraightDown,
        handleStraightUp,
        handleTextInput,
        registerSignal,
        audioInput,
        practiceText,
        setPracticeText,
        sessionStart: sessionStart.current,
        sessionStats: sessionStats.current,
        checkProgress,
        pileupCount,
        setPileupCount,
        currentSignal,
        paddleState,
        startKeyer,
        dismissTrivia: () => setTrivia(null),
        instantText,
        setInstantText,
        customChars,
        setCustomChars,
        comprehensionItem,
        comprehensionState,
        handleComprehensionAnswer,
        advanceLiteratureSegment,
        comprehensionMode,
        setComprehensionMode,
        setTarget,
        audio, // Expose audio engine for advanced modes
        playTone: (freq, durationMs, type) => audio.playTone(durationMs / 1000, freq, type),
        userSettings,
        setUserSettings,
        saveActivity,
        saveAchievement,
        saveTriviaUnlock,
        checkAchievementProgress,
        setCurrentLiteratureId,
        setCurrentSegmentIndex,
        quizQuestion,
        quizAnswer,
        quizInput,
        quizInputMode,
        setQuizInputMode,
        saveQSOLog,
        recentChars,
        inputMode
    };
};


